import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  paramsSerializer: { indexes: null },  // sérialise les tableaux sans crochets : ?ids=a&ids=b (compatible FastAPI List[str])
});

// ── Contexte société/agence (mis à jour par SocieteContext) ───────────────────
let _societeId = null;
let _agencesIds = [];

export const setApiContext = (societeId, agencesIds = []) => {
  _societeId = societeId || null;
  _agencesIds = agencesIds || [];
};

// Intercepteur pour ajouter le token + contexte société/agence
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (_societeId) config.headers['X-Societe-Id'] = _societeId;
  if (_agencesIds.length > 0) config.headers['X-Agences-Ids'] = _agencesIds.join(',');
  return config;
});

// ── Gestion du token refresh (évite les boucles infinies) ────────────────────
let _isRefreshing = false;
let _refreshQueue = []; // callbacks en attente du nouveau token

const _processQueue = (token) => {
  _refreshQueue.forEach(cb => cb(token));
  _refreshQueue = [];
};

// Intercepteur pour normaliser les erreurs Pydantic v2 (detail = tableau d'objets)
API.interceptors.response.use(
  (response) => response,
  (error) => {
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail)) {
      // Pydantic v2 : [{type, loc, msg, input, url}, ...] → chaîne lisible
      error.response.data.detail = detail
        .map(d => (typeof d === 'object' && d.msg) ? d.msg : JSON.stringify(d))
        .join(' ; ');
    }
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs 401
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url || '';

    // Ne jamais tenter le refresh pour les endpoints d'auth (évite la récursion infinie)
    if (error.response?.status !== 401 || url.includes('/api/auth/')) {
      return Promise.reject(error);
    }

    // Requête déjà retentée → abandon
    if (originalRequest._retried) {
      localStorage.removeItem('token');
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      return Promise.reject(error);
    }

    // Un refresh est déjà en cours → mettre en file d'attente
    if (_isRefreshing) {
      return new Promise((resolve, reject) => {
        _refreshQueue.push((token) => {
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(API.request(originalRequest));
          } else {
            reject(error);
          }
        });
      });
    }

    originalRequest._retried = true;
    _isRefreshing = true;

    try {
      const res = await API.post('/api/auth/refresh');
      if (res.data?.token) {
        const newToken = res.data.token;
        localStorage.setItem('token', newToken);
        API.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        _processQueue(newToken);
        return API.request(originalRequest);
      }
    } catch {
      // Refresh échoué → déconnexion propre
      _processQueue(null);
      localStorage.removeItem('token');
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    } finally {
      _isRefreshing = false;
    }

    return Promise.reject(error);
  }
);

// Auth
export const authLogin = (data) => API.post('/api/auth/login', data);
export const authRegister = (data) => API.post('/api/auth/register', data);
export const authLogout = () => API.post('/api/auth/logout');
export const authMe = () => API.get('/api/auth/me');
export const forgotPassword = (email) => API.post('/api/auth/forgot-password', { email });
export const resetPassword = (token, password) => API.post('/api/auth/reset-password', { token, password });
export const resendVerification       = ()        => API.post('/api/auth/resend-verification');
export const resendVerificationPublic = (email)   => API.post('/api/auth/resend-verification-public', { email });
export const googleSession = (sessionId) => API.post('/api/auth/google-session', { session_id: sessionId });

// Organisations (à venir)
export const getOrganisations = () => API.get('/api/organisations');
export const getCurrentOrg = () => API.get('/api/organisations/current');
export const updateCurrentOrg = (data) => API.patch('/api/organisations/current', data);
export const getPiecesJointes = (materielId) => API.get(`/api/materiels/${materielId}/pieces-jointes`);
export const uploadPieceJointe = (materielId, formData) => API.post(`/api/materiels/${materielId}/pieces-jointes`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const renamePieceJointe = (pjId, nom) => API.patch(`/api/pieces-jointes/${pjId}/rename`, { nom });
export const deletePieceJointe = (pjId) => API.delete(`/api/pieces-jointes/${pjId}`);
export const downloadPieceJointe = (pjId) => API.get(`/api/pieces-jointes/${pjId}/download`, { responseType: 'blob' });

// ── Utilisateurs ──────────────────────────────────────────────────────────────
export const getUsers = () => API.get('/api/users');
export const inviteUser = (data) => API.post('/api/users/invite', data);
export const updateUserRole = (userId, role) => API.patch(`/api/users/${userId}/role`, { role });
export const updateUserName = (userId, name) => API.patch(`/api/users/${userId}/name`, { name });
export const updateMyProfile = (data) => API.patch('/api/auth/profile', data);
export const toggleUserStatut = (userId, actif) => API.patch(`/api/users/${userId}/statut`, { actif });
export const deleteUser         = (userId) => API.delete(`/api/users/${userId}`);
export const getUserConnexions  = (userId) => API.get(`/api/users/${userId}/connexions`);
export const getUserAccess = (userId) => API.get(`/api/users/${userId}/access`);
export const updateUserAccess = (userId, access) => API.put(`/api/users/${userId}/access`, { access });
export const getMyAccess = () => API.get('/api/users/me/access');
export const getUserMenuRights = (userId) => API.get(`/api/users/${userId}/menu-rights`);
export const getMyMenuRights = () => API.get(`/api/users/me/menu-rights`);
export const updateUserMenuRights = (userId, menuRights) => API.put(`/api/users/${userId}/menu-rights`, { menu_rights: menuRights });
export const resendInvite = (userId, data) => API.post(`/api/users/${userId}/resend-invite`, data);
export const changePassword = (data) => API.post('/api/auth/change-password', data);

// ── Odoo ──────────────────────────────────────────────────────────────────────
export const pingOdoo = () => API.get('/api/odoo/ping');
export const exporterMensualiteOdoo = (financementId, data) =>
  API.post(`/api/odoo/financements/${financementId}/exporter-mensualite`, data);
export const getExportsOdoo = (financementId) =>
  API.get(`/api/odoo/financements/${financementId}/exports`);
export const annulerExportOdoo = (exportId) =>
  API.delete(`/api/odoo/exports/${exportId}`);
export const exportMensualitesMoisOdoo = (mois, annee) =>
  API.post(`/api/odoo/export-mensualites-mois?mois=${mois}&annee=${annee}`);
export const listExportsOdoo = (params) => API.get('/api/odoo/exports', { params });

// Matériels
export const getMateriels = (params = {}) => API.get('/api/materiels', { params });
export const getMaterielsStats = () => API.get('/api/materiels/stats');
// ── Pennylane OR ──────────────────────────────────────────────────────────────
export const getPlOrPdfBlob    = (ordreId)  => API.get(`/api/atelier/ordres/${ordreId}/pennylane-pdf/view`, { responseType: 'blob' });
export const validerPlOr       = (ordreId)  => API.post(`/api/atelier/ordres/${ordreId}/pennylane-valider`);
export const supprimerPlOrDraft = (ordreId) => API.delete(`/api/atelier/ordres/${ordreId}/pennylane-draft`);
export const getDashboardReparation = (params) => API.get('/api/rh/dashboard-reparation', { params });
export const getDashboardStats    = () => API.get('/api/dashboard/stats');
export const getDashboardLocation = (params = {}) => API.get('/api/dashboard/location', { params });
export const getMaterielsCategories = () => API.get('/api/materiels/categories');
export const getMateriel = (id) => API.get(`/api/materiels/${id}`);
export const getMaterielBadges = (id) => API.get(`/api/materiels/${id}/badges`);
export const createMateriel = (data) => API.post('/api/materiels', data);
export const updateMateriel = (id, data) => API.put(`/api/materiels/${id}`, data);
export const deleteMateriel = (id) => API.delete(`/api/materiels/${id}`);
export const updateMaterielStatut = (id, statut) => API.patch(`/api/materiels/${id}/statut`, { statut });
export const getMaterielCaracs = (id) => API.get(`/api/materiels/${id}/caracteristiques`);
export const saveMaterielCaracOverrides = (id, data) => API.patch(`/api/materiels/${id}/carac-overrides`, data);

// Caractéristiques techniques
export const getCaracTechniques = () => API.get('/api/carac-techniques');
export const createCaracTechnique = (data) => API.post('/api/carac-techniques', data);
export const updateCaracTechnique = (id, data) => API.patch(`/api/carac-techniques/${id}`, data);
export const deleteCaracTechnique = (id) => API.delete(`/api/carac-techniques/${id}`);
export const updateCompteurHoraire = (id, compteur) => API.patch(`/api/materiels/${id}/compteur`, { compteur_horaire: compteur });
export const previewCsvImport = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return API.post('/api/materiels/import/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const importCsv = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return API.post('/api/materiels/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const exportMaterielsCsv = () => API.get('/api/materiels/export/csv', { responseType: 'blob' });

// Disponibilités / Réservations
export const getReservations = (params = {}) => API.get('/api/disponibilites', { params });
export const getCalendrier = (params) => API.get('/api/disponibilites/calendrier', { params });
export const checkDisponibilite = (params) => API.get('/api/disponibilites/check', { params });
export const getReservation = (id) => API.get(`/api/disponibilites/${id}`);
export const createReservation = (data) => API.post('/api/disponibilites', data);
export const updateReservation = (id, data) => API.put(`/api/disponibilites/${id}`, data);
export const deleteReservation = (id) => API.delete(`/api/disponibilites/${id}`);
export const getOccupationStats = (params) => API.get('/api/disponibilites/stats/occupation', { params });

// Contacts
export const getContacts = (params = {}) => API.get('/api/contacts', { params });
export const getContactsStats = () => API.get('/api/contacts/stats');
export const getContactsTags = () => API.get('/api/contacts/tags');
export const getContact = (id) => API.get(`/api/contacts/${id}`);
export const createContact = (data) => API.post('/api/contacts', data);
export const updateContact = (id, data) => API.put(`/api/contacts/${id}`, data);
export const deleteContact = (id) => API.delete(`/api/contacts/${id}`);
export const updateContactType = (id, type) => API.patch(`/api/contacts/${id}/type`, { type_contact: type });
export const searchContactsAutocomplete = (q, type) => API.get('/api/contacts/search/autocomplete', { params: { q, type_contact: type } });

// Entreprises
export const getEntreprises = (params = {}) => API.get('/api/entreprises', { params });
export const getEntreprisesStats = () => API.get('/api/entreprises/stats');
export const getEntreprisesTags = () => API.get('/api/entreprises/tags');
export const getEntreprise = (id) => API.get(`/api/entreprises/${id}`);
export const createEntreprise = (data) => API.post('/api/entreprises', data);
export const updateEntreprise = (id, data) => API.put(`/api/entreprises/${id}`, data);
export const deleteEntreprise = (id) => API.delete(`/api/entreprises/${id}`);
export const updateEntrepriseType = (id, type) => API.patch(`/api/entreprises/${id}/type`, { type_entreprise: type });
export const toggleGelEntreprise       = (id) => API.put(`/api/entreprises/${id}/gel`);
export const archiveEntreprise         = (id) => API.put(`/api/entreprises/${id}/archive`);
export const getDuplicateColumns = () => API.get('/api/entreprises/duplicate-columns');
export const detectDuplicates = (criteria) => API.get('/api/entreprises/duplicates', { params: { criteria } });
export const mergeEntreprises = (master_id, duplicate_ids) => API.post('/api/entreprises/merge', { master_id, duplicate_ids });
export const mergePersonnes = (master_id, duplicate_ids) => API.post('/api/personnes/merge', { master_id, duplicate_ids });
export const checkDuplicatePersonne = (params) => API.get('/api/personnes/check-duplicate', { params });
export const getStatutBlocageClient = (id) => API.get(`/api/entreprises/${id}/statut-blocage`);
export const getEntrepriseMateriels = (id) => API.get(`/api/entreprises/${id}/materiels`);
export const getEntrepriseRapportPdf = (id, annee, mode = 'proprietaire') => API.get(`/api/entreprises/${id}/rapport-pdf`, {
  params: { annee, mode },
  responseType: 'blob',
});

// Annuaire Tags
export const getAnnuaireTags = () => API.get('/api/annuaire/tags');
export const getAnnuaireDepartements = () => API.get('/api/annuaire/departements');
export const getAnnuaireTagsByType = (types) => API.get('/api/annuaire/tags', { params: { types } });
export const createAnnuaireTag = (data) => API.post('/api/annuaire/tags', data);
export const updateAnnuaireTag = (id, data) => API.put(`/api/annuaire/tags/${id}`, data);
export const deleteAnnuaireTag = (id) => API.delete(`/api/annuaire/tags/${id}`);
export const getTagUsers = (tagId) => API.get(`/api/annuaire/tags/${tagId}/users`);
export const putTagUsers = (tagId, userIds) => API.put(`/api/annuaire/tags/${tagId}/users`, { user_ids: userIds });
export const getEntrepriseTags = (entrepriseId) => API.get(`/api/entreprises/${entrepriseId}/tags`);
export const applyTagToEntreprise = (entrepriseId, tagId) => API.post(`/api/entreprises/${entrepriseId}/tags`, { tag_id: tagId });
export const removeTagFromEntreprise = (entrepriseId, tagId) => API.delete(`/api/entreprises/${entrepriseId}/tags/${tagId}`);
export const getPersonneTags = (personneId) => API.get(`/api/personnes/${personneId}/tags`);
export const applyTagToPersonne = (personneId, tagId) => API.post(`/api/personnes/${personneId}/tags`, { tag_id: tagId });
export const removeTagFromPersonne = (personneId, tagId) => API.delete(`/api/personnes/${personneId}/tags/${tagId}`);
// Party Model — tags client (tiers_tags) : solvabilité/risque, partagés B2B/B2C
export const getTiersTags = (tiersId) => API.get(`/api/tiers/${tiersId}/tags`);
export const applyTagToTiers = (tiersId, tagId) => API.post(`/api/tiers/${tiersId}/tags`, { tag_id: tagId });
export const removeTagFromTiers = (tiersId, tagId) => API.delete(`/api/tiers/${tiersId}/tags/${tagId}`);

// Commandes d'achat depuis OR / Vente
export const creerAchatDepuisSource = (data) => API.post('/api/achats/depuis-source', data);
export const getAchatsEnCommande = (params) => API.get('/api/achats/en-commande', { params });
export const getCandidatsAggregation = (data) => API.post('/api/achats/candidats-aggregation', data);
export const getAchatsByDocument = (params) => API.get('/api/achats/sources-by-document', { params });
export const getAchatLinkedSources = (achatId) => API.get(`/api/achats/${achatId}/linked-sources`);

// Dossiers de Revient
export const getDossiersRevient       = (p)          => API.get('/api/dossiers-revient', { params: p });
export const getDossierRevient        = (id)         => API.get(`/api/dossiers-revient/${id}`);
export const getFacturesDisponiblesDR = (id, p)      => API.get(`/api/dossiers-revient/${id}/factures-disponibles`, { params: p });
export const createDossierRevient     = (d)          => API.post('/api/dossiers-revient', d);
export const updateDossierRevient     = (id, d)      => API.put(`/api/dossiers-revient/${id}`, d);
export const lierMaterielsDossier     = (id, d)      => API.post(`/api/dossiers-revient/${id}/materiels`, d);
export const lierFactureDossier       = (id, d)      => API.post(`/api/dossiers-revient/${id}/factures`, d);
export const detacherFactureDossier   = (id, fid)    => API.delete(`/api/dossiers-revient/${id}/factures/${fid}`);
export const ciblerMaterielsFacture   = (id, fid, d) => API.post(`/api/dossiers-revient/${id}/factures/${fid}/cibler-materiels`, d);
export const recalculerDossier        = (id)         => API.post(`/api/dossiers-revient/${id}/recalculer`);
export const cloturerDossierRevient   = (id)         => API.patch(`/api/dossiers-revient/${id}/cloturer`);
export const rouvrirDossierRevient    = (id)         => API.patch(`/api/dossiers-revient/${id}/rouvrir`);

// ─── Fusion d'articles ────────────────────────────────────────────────────────
export const previewMergeArticles = (source_id, master_id) =>
  API.post('/api/catalogue/articles/merge/preview', { source_id, master_id });
export const mergeArticles = (source_id, master_id) =>
  API.post('/api/catalogue/articles/merge', { source_id, master_id });

// Personnes
export const getPersonnes = (params = {}) => API.get('/api/personnes', { params });
export const getPersonnesStats = () => API.get('/api/personnes/stats');
export const searchPersonnes = (q, limit = 10) => API.get('/api/personnes/search', { params: { q, limit } });
export const searchTiers     = (q, limit = 15) => API.get('/api/tiers/search',    { params: { q, limit } });
export const getPersonne = (id) => API.get(`/api/personnes/${id}`);
export const createPersonne = (data) => API.post('/api/personnes', data);
export const updatePersonne = (id, data) => API.put(`/api/personnes/${id}`, data);
export const deletePersonne = (id) => API.delete(`/api/personnes/${id}`);
export const promotePersonneToEntreprise = (id) => API.post(`/api/personnes/${id}/promote-to-entreprise`);
export const setPersonnePrincipale = (id) => API.patch(`/api/personnes/${id}/principal`);
export const updatePersonneEntreprise = (id, entreprise_id) => API.patch(`/api/personnes/${id}/entreprise`, { entreprise_id });
export const addPersonneEntreprise = (id, entreprise_id) => API.patch(`/api/personnes/${id}/entreprises/add`, { entreprise_id });
export const removePersonneEntreprise = (id, entreprise_id) => API.patch(`/api/personnes/${id}/entreprises/remove`, { entreprise_id });
export const getEntrepriseComptabilite = (entreprise_id, type = 'client') =>
  API.get(`/api/entreprises/${entreprise_id}/comptabilite?type=${type}`);
export const syncLedgerTiers = (entreprise_id, type = 'client') =>
  API.post(`/api/entreprises/${entreprise_id}/sync-ledger?type=${type}`);

// Compteurs
export const getCompteurs = (params = {}) => API.get('/api/compteurs', { params });
export const createSaisie = (data) => API.post('/api/compteurs', data);
export const updateSaisie = (id, data) => API.patch(`/api/compteurs/${id}`, data);
export const deleteSaisie = (id) => API.delete(`/api/compteurs/${id}`);
// Compteurs v2 (multi-compteurs par matériel)
export const getCompteursV2 = (materielId) => API.get(`/api/materiels/${materielId}/compteurs-v2`);
export const createCompteurV2 = (materielId, data) => API.post(`/api/materiels/${materielId}/compteurs-v2`, data);
export const updateCompteurV2 = (compteurId, data) => API.patch(`/api/compteurs-v2/${compteurId}`, data);
export const getRelevesV2 = (compteurId) => API.get(`/api/compteurs-v2/${compteurId}/releves`);
export const createReleveV2 = (compteurId, data) => API.post(`/api/compteurs-v2/${compteurId}/releves`, data);
export const updateReleveV2 = (releveId, data) => API.patch(`/api/compteurs-v2/releves/${releveId}`, data);
export const deleteReleveV2 = (releveId) => API.delete(`/api/compteurs-v2/releves/${releveId}`);

// Atelier — Ordres de réparation
export const getOrdresReparation = (params) => API.get('/api/atelier/ordres', { params });
export const getOrdreReparation  = (id)     => API.get(`/api/atelier/ordres/${id}`);
export const getAtelierStats = () => API.get('/api/atelier/stats');
export const createOrdreReparation = (data) => API.post('/api/atelier/ordres', data);
export const envoyerEmailOR = (id, data) => API.post(`/api/atelier/ordres/${id}/envoyer-devis`, data);
export const deleteOrdreReparation = (id) => API.delete(`/api/atelier/ordres/${id}`);
export const updateOrdreReparation = (id, data) => API.put(`/api/atelier/ordres/${id}`, data);
export const appendTravauxOR       = (id, texte) => API.post(`/api/atelier/ordres/${id}/append-travaux`, { texte });
export const getTauxChange         = (monnaie, date) => API.get('/api/taux-change/latest', { params: { monnaie, ...(date ? { date } : {}) } });
export const creerFicheIntervention = (id)       => API.post(`/api/atelier/ordres/${id}/fiches-intervention`, {
  date_intervention: new Date().toLocaleDateString('en-CA'),  // YYYY-MM-DD en heure locale
});
export const getORFichesIntervention = (ordreId)  => API.get(`/api/atelier/ordres/${ordreId}/fiches-intervention`);
export const getFicheInterventionPdf = (ficheId)  => API.get(`/api/atelier/fiches-intervention/${ficheId}/pdf`, { responseType: 'blob' });
export const deleteFicheIntervention = (ficheId)  => API.delete(`/api/atelier/fiches-intervention/${ficheId}`);
export const getFicheParToken       = (token)    => API.get(`/api/atelier/fiche/${token}`);
export const validerFiche           = (token, d) => API.post(`/api/atelier/fiche/${token}/valider`, d);
export const togglePreFacturation   = (ordreId, data) => API.post(`/api/atelier/ordres/${ordreId}/pre-facturation`, data);
export const togglePreFacturationMO = (ordreId, data) => API.post(`/api/atelier/ordres/${ordreId}/pre-facturation-mo`, data);

// ── Motifs de non-facturation ─────────────────────────────────────────────────
export const getMotifsNonFacturation    = ()           => API.get('/api/parametres/motifs-non-facturation');
export const createMotifNonFacturation  = (data)       => API.post('/api/parametres/motifs-non-facturation', data);
export const updateMotifNonFacturation  = (id, data)   => API.put(`/api/parametres/motifs-non-facturation/${id}`, data);
export const deleteMotifNonFacturation  = (id)         => API.delete(`/api/parametres/motifs-non-facturation/${id}`);
export const getStockParAgence      = (agenceId, articleIds) => API.get('/api/atelier/stock-agence', { params: { agence_id: agenceId, article_ids: articleIds.join(',') } });
// Signature OR
export const orSignerTablette        = (id, data)     => API.post(`/api/atelier/ordres/${id}/signature/tablette`, data);
export const orEnvoyerSignatureEmail = (id, data)     => API.post(`/api/atelier/ordres/${id}/signature/email`, data);
export const orUploadDevisSigne      = (id, formData) => API.post(`/api/atelier/ordres/${id}/signature/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const orEnvoyerLienSignature  = (id, data)     => API.post(`/api/atelier/ordres/${id}/signature/lien`, data);
export const orAnnulerSignature      = (id)           => API.post(`/api/atelier/ordres/${id}/signature/annuler`);
// Signature démarrage (confirme → en_reparation)
export const orDemarrageSignerTablette = (id, data)   => API.post(`/api/atelier/ordres/${id}/demarrage/tablette`, data);
export const orDemarrageEnvoyerEmail   = (id, data)   => API.post(`/api/atelier/ordres/${id}/demarrage/email`, data);
export const orDemarrageEnvoyerLien    = (id, data)   => API.post(`/api/atelier/ordres/${id}/demarrage/lien`, data);
export const orDemarrageUploadSigne    = (id, fd)     => API.post(`/api/atelier/ordres/${id}/demarrage/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const orDemarrageAnnuler        = (id)         => API.post(`/api/atelier/ordres/${id}/demarrage/annuler`);
export const getORDemarrageToken       = (token)      => API.get(`/api/atelier/demarrer/${token}`);
export const soumettreORDemarrage      = (token, d)   => API.post(`/api/atelier/demarrer/${token}/soumettre`, d);
export const getORParToken             = (token)      => API.get(`/api/atelier/signer/${token}`);
export const signerORWeb               = (token, d)   => API.post(`/api/atelier/signer/${token}`, d);
export const changeOrdreStatut       = (id, statut)   => API.patch(`/api/atelier/ordres/${id}/statut`, { statut });
export const toggleVerrouillageOR    = (id)            => API.patch(`/api/atelier/ordres/${id}/verrouillage`);
export const terminerOrdreReparation = (id, data)     => API.post(`/api/atelier/ordres/${id}/terminer`, data);
export const syncOdooAchats         = ()       => API.post('/api/achats/sync-odoo');
export const getFacturesAchatPage   = (p = {}) => API.get('/api/factures-achat', { params: p });
export const getFactureAchatPdf     = (id)     => API.get(`/api/factures-achat/${id}/pdf`, { responseType: 'arraybuffer' });
export const getFactureAchatLignes  = (id)     => API.get(`/api/factures-achat/${id}/lignes`);
export const saveFactureAchatLignes = (id, d)  => API.post(`/api/factures-achat/${id}/lignes`, d);

export const getFacturesReparation   = (p = {})       => API.get('/api/atelier/factures', { params: p });
export const deleteFactureReparation = (id)            => API.delete(`/api/atelier/factures/${id}`);
export const getFactureReparationPdf = (id)           => API.get(`/api/atelier/factures/${id}/pdf`, { responseType: 'arraybuffer' });
export const syncFactureReparationOdoo = (id)          => API.post(`/api/atelier/factures/${id}/sync-odoo`);

// Catalogue — Unités
export const getUnites = () => API.get('/api/catalogue/unites');
export const createUnite = (data) => API.post('/api/catalogue/unites', data);
export const updateUnite = (id, data) => API.patch(`/api/catalogue/unites/${id}`, data);
export const deleteUnite = (id) => API.delete(`/api/catalogue/unites/${id}`);

// Catalogue — Contenants
export const getContenants = () => API.get('/api/catalogue/contenants');
export const createContenant = (data) => API.post('/api/catalogue/contenants', data);
export const updateContenant = (id, data) => API.patch(`/api/catalogue/contenants/${id}`, data);
export const deleteContenant = (id) => API.delete(`/api/catalogue/contenants/${id}`);

// Catalogue — Familles
export const getFamilles = (params) => API.get('/api/catalogue/familles', { params });
export const createFamille = (data) => API.post('/api/catalogue/familles', data);
export const updateFamille = (id, data) => API.put(`/api/catalogue/familles/${id}`, data);
export const deleteFamille = (id) => API.delete(`/api/catalogue/familles/${id}`);

// Catalogue — Sous-familles
export const getSousFamilles = (params) => API.get('/api/catalogue/sous-familles', { params });
export const createSousFamille = (data) => API.post('/api/catalogue/sous-familles', data);
export const updateSousFamille = (id, data) => API.put(`/api/catalogue/sous-familles/${id}`, data);
export const deleteSousFamille = (id) => API.delete(`/api/catalogue/sous-familles/${id}`);
export const updateSousFamilleCaracs = (id, data) => API.patch(`/api/catalogue/sous-familles/${id}/caracteristiques`, data);
export const fusionnerSousFamilles = (sourceId, cibleId) => API.post(`/api/catalogue/sous-familles/${sourceId}/fusionner`, { cible_id: cibleId });

// Catalogue — Articles
export const getArticles = (params) => API.get('/api/catalogue/articles', { params });
export const exportArticlesXlsx = (params) => API.get('/api/catalogue/articles/export', { params, responseType: 'blob' });
export const getArticle = (id) => API.get(`/api/catalogue/articles/${id}`);
export const createArticle = (data) => API.post('/api/catalogue/articles', data);
export const updateArticle = (id, data) => API.put(`/api/catalogue/articles/${id}`, data);
export const patchArticleFournisseurPrefere = (id, fournisseurPrefere) =>
  API.patch(`/api/catalogue/articles/${id}/fournisseur-prefere`, { fournisseur_prefere: fournisseurPrefere });
export const deleteArticle = (id) => API.delete(`/api/catalogue/articles/${id}`);
export const toggleArticleActif = (id, actif) => API.patch(`/api/catalogue/articles/${id}/actif`, { actif });
export const getArticleUsage = (id) => API.get(`/api/catalogue/articles/${id}/usage`);
export const getArticleCaracs = (id) => API.get(`/api/catalogue/articles/${id}/caracteristiques`);
export const getArticlePrevisions = (id) => API.get(`/api/catalogue/articles/${id}/previsions`);
export const getPrevisionsTresorerie = () => API.get('/api/rapports/previsions-tresorerie');
export const exportPrevisionsTresorerieExcel = () => API.get('/api/rapports/previsions-tresorerie/export/excel', { responseType: 'blob' });
export const exportPrevisionsTresoreriePDF   = () => API.get('/api/rapports/previsions-tresorerie/export/pdf',   { responseType: 'blob' });
export const getArticlePrevisionVentes = (id, agenceKey) => API.get(`/api/catalogue/articles/${id}/previsions/${agenceKey || '_'}/ventes`);
export const getArticleEquivalences      = (id)           => API.get(`/api/catalogue/articles/${id}/equivalences`);
export const addArticleEquivalence       = (id, data)     => API.post(`/api/catalogue/articles/${id}/equivalences`, data);
export const removeArticleEquivalence    = (id, eqId)     => API.delete(`/api/catalogue/articles/${id}/equivalences/${eqId}`);
export const patchArticleEquivalence     = (id, eqId, d)  => API.patch(`/api/catalogue/articles/${id}/equivalences/${eqId}`, d);
export const getArticlePrevisionOR     = (id, agenceKey) => API.get(`/api/catalogue/articles/${id}/previsions/${agenceKey || '_'}/or`);
export const getArticlePrevisionAchats = (id, agenceKey) => API.get(`/api/catalogue/articles/${id}/previsions/${agenceKey || '_'}/achats`);
export const saveArticleCaracs = (id, data) => API.patch(`/api/catalogue/articles/${id}/caracteristiques`, data);
export const getStockThresholds = (articleId) => API.get(`/api/catalogue/articles/${articleId}/stock-thresholds`);
export const updateStockThreshold = (articleId, entryId, data) => API.put(`/api/catalogue/articles/${articleId}/stock-thresholds/${entryId}`, data);
export const createStockThreshold = (articleId, data) => API.post(`/api/catalogue/articles/${articleId}/stock-thresholds`, data);
export const updateStockSeuils = (data) => API.patch(`/api/achats/stock-seuils`, data);
export const getArticleEmplacements = (articleId) => API.get(`/api/catalogue/articles/${articleId}/emplacements`);
export const initArticleEmplacement = (articleId, data) => API.post(`/api/catalogue/articles/${articleId}/emplacements`, data);
export const updateArticleEmplacement = (articleId, entryId, data) => API.patch(`/api/catalogue/articles/${articleId}/emplacements/${entryId}`, data);
export const getCatalogueStats = () => API.get('/api/catalogue/stats');
export const importArticles = (formData) => API.post('/api/catalogue/import-articles', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const linkArticlesMateriels = () => API.post('/api/catalogue/link-articles-materiels');
export const bulkUpdateArticle = (materiel_ids, article_id) =>
  API.post('/api/materiels/bulk-update-article', { materiel_ids, article_id });
export const bulkUpdateExploitant = (materiel_ids, exploitant_id, exploitant_nom, exploitant_type) =>
  API.post('/api/materiels/bulk-update-exploitant', { materiel_ids, exploitant_id, exploitant_nom, exploitant_type });
export const getChauffeur = (materiel_id) => API.get(`/api/materiels/${materiel_id}/chauffeur`);
export const assignerChauffeur = (materiel_id, data) => API.post(`/api/materiels/${materiel_id}/chauffeur`, data);
export const retirerChauffeur = (materiel_id) => API.delete(`/api/materiels/${materiel_id}/chauffeur`);
export const transferOrgOwnership = (new_owner_id) =>
  API.post('/api/org/transfer-ownership', { new_owner_id });

export const getParametres = () => API.get('/api/parametres');
export const updateParametres = (data) => API.put('/api/parametres', data);
export const getTvaRates = () => API.get('/api/parametres/tva');
export const createTvaRate = (d) => API.post('/api/parametres/tva', d);
export const updateTvaRate = (id, d) => API.put(`/api/parametres/tva/${id}`, d);
export const deleteTvaRate = (id) => API.delete(`/api/parametres/tva/${id}`);

// TVA Matricielle
export const getPays = () => API.get('/api/parametres/pays');
export const getTvaMatrice = (type) => API.get(`/api/parametres/tva-matrice/${type}`);
export const setTvaCellule = (type, data) => API.put(`/api/parametres/tva-matrice/${type}/cellule`, data);
export const deleteTvaCellule = (type, terr_id, type_id) =>
  API.delete(`/api/parametres/tva-matrice/${type}/cellule`, { params: { territorialite_id: terr_id, type_id } });
export const getTvaTerritorialites = () => API.get('/api/parametres/tva-matrice/territorialites');
export const createTvaTerritorialite = (d) => API.post('/api/parametres/tva-matrice/territorialites', d);
export const updateTvaTerritorialite = (id, d) => API.put(`/api/parametres/tva-matrice/territorialites/${id}`, d);
export const deleteTvaTerritorialite = (id) => API.delete(`/api/parametres/tva-matrice/territorialites/${id}`);
export const getTvaTypes = () => API.get('/api/parametres/tva-matrice/types');
export const createTvaType = (d) => API.post('/api/parametres/tva-matrice/types', d);
export const updateTvaType = (id, d) => API.put(`/api/parametres/tva-matrice/types/${id}`, d);
export const deleteTvaType = (id) => API.delete(`/api/parametres/tva-matrice/types/${id}`);
export const resolveTva = (type, code_postal) =>
  API.get(`/api/parametres/tva-matrice/${type}/resolve`, { params: { code_postal } });

// Matrices de prix de vente
export const getMatricesPrix = () => API.get('/api/parametres/matrices-prix');
export const updateMatricesPrix = (data) => API.put('/api/parametres/matrices-prix', data);
export const resolveMatricePrix = (data) => API.post('/api/parametres/matrices-prix/resolve', data);

export const getMarques = (params = {}) => API.get('/api/marques', { params });
export const createMarque = (data) => API.post('/api/marques', data);
export const updateMarque = (id, data) => API.patch(`/api/marques/${id}`, data);
export const deleteMarque = (id) => API.delete(`/api/marques/${id}`);

// Modèles
export const getModeles = (params = {}) => API.get('/api/modeles', { params });
export const createModele = (data) => API.post('/api/modeles', data);
export const updateModele = (id, data) => API.patch(`/api/modeles/${id}`, data);
export const deleteModele = (id) => API.delete(`/api/modeles/${id}`);
export const setModeleGamme = (id, gamme_location_id) => API.put(`/api/modeles/${id}/gamme`, { gamme_location_id });

// Sociétés & Agences
export const getSocietes = () => API.get('/api/societes');
export const createSociete = (data) => API.post('/api/societes', data);
export const updateSociete = (id, data) => API.put(`/api/societes/${id}`, data);
export const deleteSociete = (id) => API.delete(`/api/societes/${id}`);
export const getAgences = (params = {}) => API.get('/api/agences', { params });
export const createAgence = (data) => API.post('/api/agences', data);
export const updateAgence = (id, data) => API.put(`/api/agences/${id}`, data);
export const deleteAgence = (id) => API.delete(`/api/agences/${id}`);
export const getAllAutorisations = () => API.get('/api/agences-autorisations');
export const addAutorisation = (ownerId, utilisatriceId) => API.post(`/api/agences/${ownerId}/autorisations/${utilisatriceId}`);
export const removeAutorisation = (ownerId, utilisatriceId) => API.delete(`/api/agences/${ownerId}/autorisations/${utilisatriceId}`);

export const getExploitantsListe = () => API.get('/api/materiels/exploitants-liste');
export const dupliquerControleType = (ct_id) => API.post(`/api/controles-periodiques/types/${ct_id}/dupliquer`);

// ── Transfert Comptable (Odoo → Bedee → PennyLane) ─────────────────────────
export const tcGetSocietes          = ()     => API.get('/api/transfert-comptable/societes');
export const tcGetConfig            = ()     => API.get('/api/transfert-comptable/config');
export const tcSaveConfig           = (data) => API.post('/api/transfert-comptable/config', data);
export const tcGetStats             = ()     => API.get('/api/transfert-comptable/stats');
export const tcGetJobStatus         = (id)   => API.get(`/api/transfert-comptable/sync-status/${id}`);
export const tcExtrairePlanComptable= (data) => API.post('/api/transfert-comptable/extraire/plan-comptable', data);
export const tcGetPlanComptable     = (p={}) => API.get('/api/transfert-comptable/plan-comptable', { params: p });
export const tcExtraireEcritures    = (data) => API.post('/api/transfert-comptable/extraire/ecritures', data);
export const tcGetEcritures         = (p={}) => API.get('/api/transfert-comptable/ecritures', { params: p });
export const tcExtraireJournaux     = (data) => API.post('/api/transfert-comptable/extraire/journaux', data);
export const tcGetJournaux          = (p={}) => API.get('/api/transfert-comptable/journaux', { params: p });
export const tcGetJournalMapping    = (p={}) => API.get('/api/transfert-comptable/journal-mapping', { params: p });
export const tcSaveJournalMapping   = (d)   => API.put('/api/transfert-comptable/journal-mapping', d);
export const tcExtraireFactures     = (data) => API.post('/api/transfert-comptable/extraire/factures', data);
export const tcGetFactures          = (p={}) => API.get('/api/transfert-comptable/factures', { params: p });
export const tcGetFacturePdf        = (id)   => API.get(`/api/transfert-comptable/factures/${id}/pdf`, { responseType: 'arraybuffer' });
export const tcPlGetJournaux        = ()     => API.get('/api/transfert-comptable/pennylane/journaux');
export const tcPlGetStats           = (p={}) => API.get('/api/transfert-comptable/pennylane/stats', { params: p });
export const tcPlEnvoyer            = (data) => API.post('/api/transfert-comptable/pennylane/envoyer', data);
export const tcPlResetErrors        = (p={}) => API.post('/api/transfert-comptable/pennylane/reset-errors', null, { params: p });
export const tcPlSyncTiers          = (data) => API.post('/api/transfert-comptable/pennylane/sync-tiers', data);
export const tcPlGetDossiers        = ()     => API.get('/api/transfert-comptable/pennylane/dossiers');
export const tcPlSyncEntreprise     = (data) => API.post('/api/transfert-comptable/pennylane/sync-entreprise', data);


// Maintenance Préventive — Plans d'entretien (gammes)
export const getGammesEntretien       = ()          => API.get('/api/maintenance/gammes');
export const createGammeEntretien     = (data)      => API.post('/api/maintenance/gammes', data);
export const updateGammeEntretien     = (id, data)  => API.put(`/api/maintenance/gammes/${id}`, data);
export const deleteGammeEntretien     = (id)        => API.delete(`/api/maintenance/gammes/${id}`);
export const resolveGammeMateriel     = (matId)     => API.get(`/api/preventif/resolve/${matId}`);

// Étapes (gestion individuelle)
export const createEtapeEntretien     = (planId, data) => API.post(`/api/maintenance/gammes/${planId}/etapes`, data);
export const updateEtapeEntretien     = (etapeId, data) => API.put(`/api/maintenance/etapes/${etapeId}`, data);
export const deleteEtapeEntretien     = (etapeId)  => API.delete(`/api/maintenance/etapes/${etapeId}`);

// Maintenance Préventive — Préventif (machine à états)
export const getPreventifs            = (params)    => API.get('/api/preventif', { params });
export const getPreventifStats        = ()          => API.get('/api/preventif/stats');
export const getPreventif             = (id)        => API.get(`/api/preventif/${id}`);
export const transitionPreventif      = (id, data)  => API.post(`/api/preventif/${id}/transition`, data);
export const convertirPreventifEnOR   = (id)        => API.post(`/api/preventif/${id}/convertir-en-or`);
export const relancerMailPreventif    = (id)        => API.post(`/api/preventif/${id}/relancer-mail`);
export const envoyerEmailPreventif    = (id)        => API.post(`/api/preventif/${id}/envoyer-email`);
export const runBatchPreventif        = ()          => API.post('/api/preventif/batch');

// Legacy aliases
export const getPlansEntretien        = ()          => API.get('/api/maintenance/gammes');
export const createPlanEntretien      = (data)      => API.post('/api/maintenance/gammes', data);
export const updatePlanEntretien      = (id, data)  => API.put(`/api/maintenance/gammes/${id}`, data);
export const deletePlanEntretien      = (id)        => API.delete(`/api/maintenance/gammes/${id}`);

// Financements
export const getFinancements = (params = {}) => API.get('/api/financements', { params });
export const getFinancementsStats = () => API.get('/api/financements/stats');
export const getFinancementsRapport = () => API.get('/api/financements/rapport');
export const immobiliserMateriels = (financement_id, data) =>
  API.post(`/api/financements/${financement_id}/immobiliser`, data);
export const getFinancement = (id) => API.get(`/api/financements/${id}`);
export const createFinancement = (data) => API.post('/api/financements', data);
export const updateFinancement = (id, data) => API.put(`/api/financements/${id}`, data);
export const deleteFinancement = (id) => API.delete(`/api/financements/${id}`);
export const uploadFinancementDocument = (id, formData) =>
  API.post(`/api/financements/${id}/document`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteFinancementDocument = (id) => API.delete(`/api/financements/${id}/document`);

// Parc matériels
export const changerParcMateriel = (id, data) => API.post(`/api/materiels/${id}/changer-parc`, data);
export const getMouvementsParc = (id) => API.get(`/api/materiels/${id}/mouvements-parc`);
export const transfererAgence = (id, data) => API.post(`/api/materiels/${id}/transferer-agence`, data);
export const getTransfertsAgence = (id) => API.get(`/api/materiels/${id}/transferts`);

// Contrôles périodiques
export const getControleTypes = () => API.get('/api/controles-periodiques/types');
export const createControleType = (data) => API.post('/api/controles-periodiques/types', data);
export const updateControleType = (id, data) => API.put(`/api/controles-periodiques/types/${id}`, data);
export const deleteControleType = (id) => API.delete(`/api/controles-periodiques/types/${id}`);
export const deleteControleRealisation = (crId) => API.delete(`/api/controles-periodiques/realisations/${crId}`);
export const getControlesAEffectuer = (params = {}) => API.get('/api/controles-periodiques/a-effectuer', { params });
export const getControleRealisations = (params = {}) => API.get('/api/controles-periodiques/realisations', { params });
export const createControleRealisation = (data) => API.post('/api/controles-periodiques/realisations', data);
export const updateControleRealisation = (id, data) => API.put(`/api/controles-periodiques/realisations/${id}`, data);
export const demarrerControle = (data) => API.post('/api/controles-periodiques/demarrer', data);
export const terminerControle = (id, formData) => API.post(
  `/api/controles-periodiques/realisations/${id}/terminer`,
  formData
);
export const uploadPjControle = (id, formData) => API.post(
  `/api/controles-periodiques/realisations/${id}/upload-pj`,
  formData
);
export const downloadPjControle = (crId) => API.get(
  `/api/controles-periodiques/realisations/${crId}/pj/download`,
  { responseType: 'blob' }
);

// Comptages (inventaire)
export const getComptages = () => API.get('/api/comptages');
export const getComptage = (id) => API.get(`/api/comptages/${id}`);
export const createComptage = (data) => API.post('/api/comptages', data);
export const deleteComptage = (id) => API.delete(`/api/comptages/${id}`);
export const marquerStatutComptage = (comptageId, materielId, data) =>
  API.put(`/api/comptages/${comptageId}/materiels/${materielId}`, data);
export const terminerComptage = (id) => API.put(`/api/comptages/${id}/terminer`);
export const previewComptage = (filtres) => API.post('/api/comptages-preview', filtres);
// Plans de comptage
export const getPlansComptage = () => API.get('/api/plans-comptage');
export const createPlanComptage = (data) => API.post('/api/plans-comptage', data);
export const updatePlanComptage = (id, data) => API.put(`/api/plans-comptage/${id}`, data);
export const deletePlanComptage = (id) => API.delete(`/api/plans-comptage/${id}`);
export const lancerPlanComptage = (id) => API.post(`/api/plans-comptage/${id}/lancer`);

// Stocks
export const getStocks = () => API.get('/api/stocks');
export const getStock = (id) => API.get(`/api/stocks/${id}`);
// Helper: extract zones from a stock's detail
export const getZones = async (stockId) => {
  const resp = await API.get(`/api/stocks/${stockId}`);
  return { data: resp.data?.zones || [] };
};
export const createStock = (data) => API.post('/api/stocks', data);
export const updateStock = (id, data) => API.put(`/api/stocks/${id}`, data);
export const deleteStock = (id) => API.delete(`/api/stocks/${id}`);
export const createZone = (stockId, data) => API.post(`/api/stocks/${stockId}/zones`, data);
export const updateZone = (stockId, zoneId, data) => API.put(`/api/stocks/${stockId}/zones/${zoneId}`, data);
export const deleteZone = (stockId, zoneId) => API.delete(`/api/stocks/${stockId}/zones/${zoneId}`);
export const getStockArticles = (stockId, params) => API.get(`/api/stocks/${stockId}/articles`, { params });
export const ajusterStockArticle = (stockId, data) => API.post(`/api/stocks/${stockId}/articles`, data);
export const getArticlesParZone   = (stockId, params = {}) => {
  const qs = new URLSearchParams();
  if (params.search)  qs.set('search',  params.search);
  if (params.zone_id) qs.set('zone_id', params.zone_id);
  if (params.limit)   qs.set('limit',   params.limit);
  const q = qs.toString();
  return API.get(`/api/stocks/${stockId}/articles-par-zone${q ? '?' + q : ''}`);
};
export const deplacerStock = (data) => API.post('/api/stocks/deplacer', data);
export const getArticlesEmplacements = () => API.get('/api/stocks/articles/emplacements');
export const getStockAlerte = (article_id, quantite = 1, agence_id = null) => API.get(`/api/stocks/articles/${article_id}/alerte`, { params: { quantite, ...(agence_id ? { agence_id } : {}) } });
export const creerDemandeTransfert = (data) => API.post('/api/demandes-transfert', data);
export const getDemandesTransfert = (params) => API.get('/api/demandes-transfert', { params });

// Annonces globales
export const getActiveAnnouncements  = ()         => API.get('/api/announcements/active');
export const getAdminAnnouncements   = ()         => API.get('/api/admin/announcements');
export const createAnnouncement      = (data)     => API.post('/api/admin/announcements', data);
export const patchAnnouncement       = (id, data) => API.patch(`/api/admin/announcements/${id}`, data);
export const deleteAnnouncement      = (id)       => API.delete(`/api/admin/announcements/${id}`);

// Notifications
export const getNotifications = () => API.get('/api/notifications');
export const markNotifLue = (id) => API.patch(`/api/notifications/${id}/lu`);
export const markAllNotifsLues = () => API.post('/api/notifications/marquer-tout-lu');
export const deleteNotif = (id) => API.delete(`/api/notifications/${id}`);
export const getStockMouvements = (stockId, limit = 100) =>
  API.get(`/api/stocks/mouvements?stock_id=${stockId}&limit=${limit}`);

export const getMouvementsGlobal = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.type_mvt)    qs.set('type_mvt',    params.type_mvt);
  if (params.stock_ids)   qs.set('stock_ids',   params.stock_ids);
  if (params.agence_id)   qs.set('agence_id',   params.agence_id);
  if (params.search)      qs.set('search',      params.search);
  if (params.commentaire) qs.set('commentaire', params.commentaire);
  if (params.limit)       qs.set('limit',       params.limit);
  if (params.offset)      qs.set('offset',      params.offset);
  return API.get(`/api/stocks/mouvements/global?${qs.toString()}`);
};

// Motifs d'ajustement inventaire
export const getMotisAjustement      = ()               => API.get('/api/stocks/motifs-ajustement');
export const createMotifAjustement   = (data)           => API.post('/api/stocks/motifs-ajustement', data);
export const updateMotifAjustement   = (id, data)       => API.put(`/api/stocks/motifs-ajustement/${id}`, data);
export const deleteMotifAjustement   = (id)             => API.delete(`/api/stocks/motifs-ajustement/${id}`);
export const searchStockArticles     = (q = '')         => API.get('/api/stocks/articles/search', { params: { q } });

// Odoo
export const getOdooWarehouses = () => API.get('/api/odoo/warehouses');
export const getSyncJobStatus = (jobId) => API.get(`/api/odoo/sync-status/${jobId}`);
export const getGhostStockCount = () => API.get('/api/stocks/ghost-count');
export const getStocksValorisation    = (params) => API.get('/api/stocks/valorisation', { params });
export const getStocksValorisationPdf = (params) => API.get('/api/stocks/valorisation/pdf', { params, responseType: 'blob' });
export const getArticlesSansPmp    = ()       => API.get('/api/stocks/articles-sans-pmp');
export const postPmpInit           = (items)  => API.post('/api/stocks/pmp-init', items);
export const uploadArticleImage = (articleId, formData) => API.post(`/api/catalogue/articles/${articleId}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteArticleImage = (articleId) => API.delete(`/api/catalogue/articles/${articleId}/image`);
export const getSyncStatus = (jobId) => API.get(`/api/odoo/sync-status/${jobId}`);
export const syncAnnuaireFromOdoo = (force_full = false) => API.post('/api/odoo/sync-annuaire', null, { params: force_full ? { force_full: true } : {} });
export const syncOdooIbans = () => API.post('/api/odoo/sync-ibans');
// sync/articles-materiels et sync/materiels supprimés (articles matériel gérés uniquement dans Bedee)
export const getFacturesLocation = (contrat_id) => API.get(`/api/locations/contrats/${contrat_id}/factures`);
export const creerFactureLocation = (contrat_id, data) => API.post(`/api/locations/contrats/${contrat_id}/factures`, data);
export const supprimerFactureLocation = (facture_id) => API.delete(`/api/locations/factures/${facture_id}`);

// Pennylane — factures location
export const viewPlFacturePdf = (facture_id) => `${API.defaults.baseURL}/api/locations/factures/${facture_id}/pennylane-pdf/view`;
export const getPlFactureLocPdfBlob = (facture_id) => API.get(`/api/locations/factures/${facture_id}/pennylane-pdf/view`, { responseType: 'blob' });
export const getFactureAcquitteeBlob = (facture_id) => API.get(`/api/factures/${facture_id}/acquittee.pdf`, { responseType: 'blob' });

// ─── Facturation unifiée ──────────────────────────────────────────────────────
export const getFacturesUnifiees    = (params)     => API.get('/api/facturation/factures', { params });
export const getFactureUnifieePdf   = (facture_id) => API.get(`/api/facturation/factures/${facture_id}/pdf`, { responseType: 'blob' });
export const getReglementsUnifies   = (params)     => API.get('/api/facturation/reglements', { params });
export const validerPlFactureLoc = (facture_id) => API.post(`/api/locations/factures/${facture_id}/pennylane-valider`);
export const validerPlFactureLocV2 = (facture_id) => API.post(`/api/locations-v2/factures/${facture_id}/pennylane-valider`);
export const supprimerPlDraftLoc = (facture_id) => API.delete(`/api/locations/factures/${facture_id}/pennylane-draft`);

// Règlements — endpoint unifié (Ventes, Location, Atelier)
export const getReglementsFac    = (facture_id) => API.get(`/api/facturation/factures/${facture_id}/reglement`);
export const createReglementFac  = (facture_id, data) => API.post(`/api/facturation/factures/${facture_id}/reglement`, data);
export const deleteReglementFac  = (facture_id, reglement_id) => API.delete(`/api/facturation/factures/${facture_id}/reglement/${reglement_id}`);
export const resyncPendingReglements = (data = {}) => API.post('/api/pennylane/reglements/resync-pending', data);

// Règlements — factures location (conservés pour rétrocompatibilité)
export const getReglementsFactureLoc  = (facture_id) => API.get(`/api/locations/factures/${facture_id}/reglement`);
export const createReglementFactureLoc = (facture_id, data) => API.post(`/api/locations/factures/${facture_id}/reglement`, data);
export const deleteReglementFactureLoc = (facture_id, reglement_id) => API.delete(`/api/locations/factures/${facture_id}/reglement/${reglement_id}`);
export const syncFactureOdoo = (facture_id) => API.post(`/api/locations/factures/${facture_id}/sync-odoo`);
export const getAideFacturation = (params) => API.get('/api/locations/aide-facturation', { params });
export const getAllFacturesLocation = (params) => API.get('/api/locations/factures', { params });

// Achats
export const getAchats = (params) => API.get('/api/achats', { params });
export const getAchat = (id) => API.get(`/api/achats/${id}`);
export const createAchat = (data) => API.post('/api/achats', data);
export const updateAchat = (id, data) => API.put(`/api/achats/${id}`, data);
// Révisions & Activité Achats
export const createAchatRevision = (id) => API.post(`/api/achats/${id}/revisions`);
export const getAchatRevisions = (id) => API.get(`/api/achats/${id}/revisions`);
export const rappelerRevisionAchat = (id, revId) => API.post(`/api/achats/${id}/revisions/${revId}/rappeler`);
export const getAchatActivite = (id) => API.get(`/api/achats/${id}/activite`);
// Documents Achats
export const getAchatDocuments = (id) => API.get(`/api/achats/${id}/documents`);
export const uploadAchatDocument = (id, fd) => API.post(`/api/achats/${id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const patchAchatDocument = (id, docId, data) => API.patch(`/api/achats/${id}/documents/${docId}`, data);
export const deleteAchatDocument = (id, docId) => API.delete(`/api/achats/${id}/documents/${docId}`);
export const downloadAchatDocument = (id, docId) => API.get(`/api/achats/${id}/documents/${docId}/download`, { responseType: 'blob' });
export const deleteAchat = (id) => API.delete(`/api/achats/${id}`);
export const getMaterielsEnStockParArticle = (article_id, agence_id, exclude_bl_id) =>
  API.get('/api/bl-ventes/materiels-en-stock', { params: { article_id, agence_id, exclude_bl_id } });
export const changeStadeAchat = (id, data) => API.patch(`/api/achats/${id}/stade`, data);
export const getAchatBonsReception = (achatId) => API.get(`/api/achats/${achatId}/bons-reception`);
export const enregistrerReceptionAchat = (id, data) => API.post(`/api/achats/${id}/reception`, data);
// PDF + Email + Confirmation
export const getAchatPdf = (id) => API.get(`/api/achats/${id}/pdf`, { responseType: 'blob' });

// Acomptes (virements SEPA)
export const getAcomptes = (params) => API.get('/api/acomptes', { params });
export const getAcompte = (id) => API.get(`/api/acomptes/${id}`);
export const createAcompte = (data) => API.post('/api/acomptes', data);
export const updateAcompte = (id, data) => API.put(`/api/acomptes/${id}`, data);
export const deleteAcompte = (id) => API.delete(`/api/acomptes/${id}`);
export const getAcompteIbanEmetteur = () => API.get('/api/acomptes/iban-emetteur');
export const exportAcomptesXml = (data) => API.post('/api/acomptes/export-xml', data, { responseType: 'blob' });

// Remises de virement SEPA
export const getRemises = (params) => API.get('/api/remises', { params });
export const getRemise = (id) => API.get(`/api/remises/${id}`);
export const createRemise = (data) => API.post('/api/remises', data);
export const deleteRemise = (id) => API.delete(`/api/remises/${id}`);
export const verserRemise = (id) => API.post(`/api/remises/${id}/verser`);
export const downloadRemiseXml = (id) => API.get(`/api/remises/${id}/xml`, { responseType: 'blob' });

// Discussions (chatter)
export const getDiscussions = (params) => API.get('/api/discussions', { params });
export const createDiscussion = (data) => API.post('/api/discussions', data);
export const getDiscussionByRecord = (record_type, record_id) => API.get('/api/discussions/by-record', { params: { record_type, record_id } });
export const getDiscussionMessages = (threadId) => API.get(`/api/discussions/${threadId}/messages`);
export const postDiscussionMessage = (threadId, corps, mentions = []) => API.post(`/api/discussions/${threadId}/messages`, { corps, mentions });
export const inviteToDiscussion = (threadId, user_ids) => API.post(`/api/discussions/${threadId}/invite-users`, { user_ids });
export const toggleDiscussionFollow = (threadId, show) => API.put(`/api/discussions/${threadId}/follow`, { show_in_dashboard: show });
export const toggleDiscussionPin    = (threadId, pin)  => API.put(`/api/discussions/${threadId}/pin`,    { is_pinned: pin });
export const addDiscussionParticipant = (threadId, data) => API.post(`/api/discussions/${threadId}/participants`, data);
export const envoyerEmailAchat = (id, data) => API.post(`/api/achats/${id}/envoyer-email`, data);
export const confirmerCommandeAchat = (id) => API.post(`/api/achats/${id}/confirmer`);

// Bons de Réception
export const getBonsReception = (params) => API.get('/api/bons-reception', { params });
export const getBonReception = (id) => API.get(`/api/bons-reception/${id}`);
export const initBonReceptionFromAchat = (achatId) => API.post(`/api/bons-reception/init-from-achat/${achatId}`);
export const createBonReception = (data) => API.post('/api/bons-reception', data);
export const updateBonReception = (id, data) => API.put(`/api/bons-reception/${id}`, data);
export const validerReception = (id) => API.patch(`/api/bons-reception/${id}/valider`);
export const deleteBonReception = (id) => API.delete(`/api/bons-reception/${id}`);
export const updateBrMaterielInstance = (brId, instanceId, data) => API.patch(`/api/bons-reception/${brId}/materiels/${instanceId}`, data);
export const envoyerFactureAchat = (id, fichier) => {
  const form = new FormData();
  form.append('fichier', fichier);
  return API.post(`/api/achats/${id}/facture`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// ── Factures Achat (table factures_achat) ──────────────────────────────────
export const getFacturesAchat    = (achat_id)         => API.get(`/api/achats/${achat_id}/factures`);
export const getAchatEcheances   = (achat_id)         => API.get(`/api/achats/${achat_id}/echeances`);
export const createAchatEcheance = (achat_id, data)   => API.post(`/api/achats/${achat_id}/echeances`, data);
export const updateAchatEcheance = (achat_id, eid, d) => API.patch(`/api/achats/${achat_id}/echeances/${eid}`, d);
export const deleteAchatEcheance = (achat_id, eid)    => API.delete(`/api/achats/${achat_id}/echeances/${eid}`);
export const syncTiersPennylane      = (societe_id)       => API.post('/api/pennylane/sync-tiers', { societe_id });
export const getSyncTiersStatus      = (job_id)            => API.get(`/api/pennylane/sync-tiers-status/${job_id}`);
export const syncAllTiersPennylane   = ()                  => API.post('/api/pennylane/sync-tiers-all');
export const matchTiersPennylane     = (entreprise_id, societe_id, role) => API.post(`/api/pennylane/match-tiers/${entreprise_id}`, { societe_id, force: true, ...(role ? { role } : {}) });
export const syncParticulierToPL    = (personne_id, societe_id)  => API.post(`/api/pennylane/particuliers/${personne_id}/sync`, societe_id ? { societe_id } : {});
export const syncPlPlanComptable     = ()                  => API.post('/api/integrations/pennylane/sync-plan-comptable');

// Catégories Pennylane
export const syncPlCategories        = (societe_id)          => API.post(`/api/pennylane/sync-categories?societe_id=${societe_id}`);
export const getPlCategories         = (societe_id)          => API.get('/api/pennylane/categories', { params: { societe_id } });
export const getPlCategoryMappings   = (societe_id)          => API.get('/api/pennylane/category-mappings', { params: { societe_id } });
export const upsertPlCategoryMapping = (data)                => API.put('/api/pennylane/category-mappings', data);
export const deletePlCategoryMapping = (id)                  => API.delete(`/api/pennylane/category-mappings/${id}`);
export const getAnnuaireLock     = ()                 => API.get('/api/organisations/annuaire-lock');
export const setAnnuaireLock     = (locked)           => API.patch('/api/organisations/annuaire-lock', { annuaire_locked: locked });
export const creerFactureAchat   = (achat_id, data)   => API.post(`/api/achats/${achat_id}/factures`, data);
export const updateFactureAchat  = (facture_id, data) => API.patch(`/api/factures-achat/${facture_id}`, data);
export const deleteFactureAchat  = (facture_id)       => API.delete(`/api/factures-achat/${facture_id}`);
export const sendFactureToOdoo   = (facture_id)       => API.post(`/api/factures-achat/${facture_id}/send-to-odoo`);
export const envoyerFacture      = (facture_id)       => API.post(`/api/factures-achat/${facture_id}/envoyer`);
export const getEnvoyerFactureStatus = (job_id)       => API.get(`/api/factures-achat/envoyer-status/${job_id}`);
export const getTauxPourDate     = (devise, date)     => API.get('/api/factures-achat/taux', { params: { devise, date } });
export const uploadPdfFactureAchat  = (facture_id, formData) => API.post(`/api/factures-achat/${facture_id}/upload-pdf`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deletePdfFactureAchat  = (facture_id)    => API.delete(`/api/factures-achat/${facture_id}/pdf`);
export const getBrsDeFacture        = (facture_id)    => API.get(`/api/factures-achat/${facture_id}/bons-reception`);
export const createBrFromFacture    = (facture_id)    => API.post(`/api/bons-reception/from-facture/${facture_id}`);
export const getBesoinAppro = (agenceIds = []) => {
  const params = agenceIds.length ? { agences: agenceIds.join(',') } : {};
  return API.get('/api/achats/besoins-appro', { params });
};
export const creerCommandesDepuisBesoins = (data) => API.post('/api/achats/besoins-appro/creer-commandes', data);

// Inventaire pièces détachées
export const previewInventaire = (data) => API.post('/api/inventaires-preview', data);
export const createInventaire = (data) => API.post('/api/inventaires', data);
export const getInventaireOptions  = (stockId) => API.get(`/api/inventaires/options/${stockId}`);
export const getInventaires        = (stockId, agenceIds) => {
  const params = {};
  if (stockId) params.stock_id = stockId;
  if (agenceIds?.length) params.agence_ids = agenceIds.join(',');
  return API.get('/api/inventaires', { params });
};
export const getInventaire         = (id) => API.get(`/api/inventaires/${id}`);
export const updateInventaireLigne = (sessionId, articleId, data) => API.patch(`/api/inventaires/${sessionId}/lignes/${articleId}`, data);
export const ajouterLigneInventaire = (sessionId, data) => API.post(`/api/inventaires/${sessionId}/lignes`, data);
export const cloturerInventaire = (id, data) => API.post(`/api/inventaires/${id}/cloture`, data);
export const exportInventairePdf = (id) => API.get(`/api/inventaires/${id}/pdf`, { responseType: 'blob' });
export const deleteInventaire = (id) => API.delete(`/api/inventaires/${id}`);

// Locations
export const getLocationCategories = () => API.get('/api/locations/categories');
export const createLocationCategorie = (data) => API.post('/api/locations/categories', data);
export const updateLocationCategorie = (id, data) => API.patch(`/api/locations/categories/${id}`, data);
export const deleteLocationCategorie = (id) => API.delete(`/api/locations/categories/${id}`);

export const getLocationGammes = (params) => API.get('/api/locations/gammes', { params });
export const exportLocationGammes = () => API.get('/api/locations/gammes/export');
export const getLocationGamme = (id) => API.get(`/api/locations/gammes/${id}`);
export const createLocationGamme = (data) => API.post('/api/locations/gammes', data);
export const updateLocationGamme = (id, data) => API.patch(`/api/locations/gammes/${id}`, data);
export const deleteLocationGamme = (id) => API.delete(`/api/locations/gammes/${id}`);

export const getMaterielsDisponibles = (params) => API.get('/api/locations/materiels-disponibles', { params });

export const getMotifsSuspension    = ()           => API.get('/api/locations/motifs-suspension');
export const createMotifSuspension  = (body)        => API.post('/api/locations/motifs-suspension', body);
export const updateMotifSuspension  = (id, body)    => API.patch(`/api/locations/motifs-suspension/${id}`, body);
export const deleteMotifSuspension  = (id)          => API.delete(`/api/locations/motifs-suspension/${id}`);

// ── Location V2 (module isolé — Phase 1) ─────────────────────────────────────
export const getContratsV2       = (params) => API.get('/api/locations-v2/contrats', { params });
export const getContratsV2Kpis   = (params) => API.get('/api/locations-v2/contrats/kpis', { params });
export const getContratV2        = (id) => API.get(`/api/locations-v2/contrats/${id}`);
export const createContratV2     = (data) => API.post('/api/locations-v2/contrats', data);
export const updateContratV2     = (id, data) => API.patch(`/api/locations-v2/contrats/${id}`, data);
export const deleteContratV2          = (id) => API.delete(`/api/locations-v2/contrats/${id}`);
export const transitionContratV2      = (id, data) => API.post(`/api/locations-v2/contrats/${id}/transition`, data);
export const annulerContratV2         = (id, data) => API.post(`/api/locations-v2/contrats/${id}/annuler`, data);
export const remettreEnBrouillonV2    = (id) => API.post(`/api/locations-v2/contrats/${id}/remettre-en-brouillon`);
export const addLigneContratV2   = (contratId, data) => API.post(`/api/locations-v2/contrats/${contratId}/lignes`, data);
export const updateLigneV2       = (ligneId, data) => API.patch(`/api/locations-v2/lignes/${ligneId}`, data);
export const deleteLigneV2       = (ligneId) => API.delete(`/api/locations-v2/lignes/${ligneId}`);
export const getMaterielsDisponiblesV2 = (contratId, ligneId) => API.get(`/api/locations-v2/contrats/${contratId}/materiels-disponibles`, { params: { ligne_id: ligneId } });
export const reserverEquipementV2      = (contratId, ligneId, body) => API.patch(`/api/locations-v2/${contratId}/lignes/${ligneId}/reserver`, body);
export const getBLsV2            = (contratId) => API.get(`/api/locations-v2/contrats/${contratId}/bl`);
export const createBLV2          = (contratId, data) => API.post(`/api/locations-v2/contrats/${contratId}/bl`, data);
export const getAvenantsV2                 = (contratId) => API.get(`/api/locations-v2/contrats/${contratId}/avenants`);
export const createAvenantV2               = (contratId, data) => API.post(`/api/locations-v2/contrats/${contratId}/avenants`, data);
export const createAvenantSessionV2        = (contratId, data) => API.post(`/api/locations-v2/contrats/${contratId}/avenants/session`, data);
export const validerAvenantV2              = (avenantId) => API.post(`/api/locations-v2/avenants/${avenantId}/valider`);
// Signature avenant
export const avenantEnvoiSignatureTerrain  = (avenantId, data) => API.post(`/api/locations-v2/avenants/${avenantId}/envoi-signature`, data);
export const avenantSignerTerrain          = (avenantId, data) => API.post(`/api/locations-v2/avenants/${avenantId}/signer-terrain`, data);
export const avenantEnvoiSignatureMail     = (avenantId, data) => API.post(`/api/locations-v2/avenants/${avenantId}/envoi-signature-mail`, data);
export const avenantEnvoiSignatureLien     = (avenantId, data) => API.post(`/api/locations-v2/avenants/${avenantId}/envoi-signature-lien`, data);
export const avenantSignatureRecue         = (avenantId) => API.post(`/api/locations-v2/avenants/${avenantId}/signature-recue`);
export const annulerAvenantV2    = (avenantId) => API.post(`/api/locations-v2/avenants/${avenantId}/annuler`);
export const getSuspensionsV2    = (contratId) => API.get(`/api/locations-v2/contrats/${contratId}/suspensions`);
export const createSuspensionV2  = (contratId, data) => API.post(`/api/locations-v2/contrats/${contratId}/suspensions`, data);
export const deleteSuspensionV2  = (suspensionId) => API.delete(`/api/locations-v2/suspensions/${suspensionId}`);
export const getServicesV2       = (contratId) => API.get(`/api/locations-v2/contrats/${contratId}/services`);
export const createServiceV2     = (contratId, data) => API.post(`/api/locations-v2/contrats/${contratId}/services`, data);
export const patchServiceV2      = (serviceId, data) => API.patch(`/api/locations-v2/services/${serviceId}`, data);
export const deleteServiceV2     = (serviceId) => API.delete(`/api/locations-v2/services/${serviceId}`);
export const getFacturesV2       = (contratId) => API.get(`/api/locations-v2/contrats/${contratId}/factures`);
export const facturerContratV2   = (contratId, data) => API.post(`/api/locations-v2/contrats/${contratId}/facturer`, data);
export const getDestinatairesFactureV2 = (contratId) => API.get(`/api/locations-v2/contrats/${contratId}/destinataires-facture`);
export const envoyerFactureEmailV2     = (contratId, payload) => API.post(`/api/locations-v2/contrats/${contratId}/envoyer-facture`, payload);
export const envoyerEmailContratDevisV2 = (id, data) => API.post(`/api/locations-v2/contrats/${id}/envoyer-devis`, data);
export const envoyerEmailAvenantDevisV2  = (id, data) => API.post(`/api/locations-v2/avenants/${id}/envoyer-devis`, data);
export const envoyerContratSigneV2       = (id, data) => API.post(`/api/locations-v2/contrats/${id}/envoi-contrat-signe`, data);
export const preparerBLV2               = (contratId, data) => API.post(`/api/locations-v2/contrats/${contratId}/preparer-bl`, data);
export const supprimerBLV2              = (blId) => API.delete(`/api/locations-v2/bls/${blId}`);
export const terrainListBLV2            = () => API.get('/api/locations-v2/terrain/bons-livraison');
export const terrainGetBLV2             = (blId) => API.get(`/api/locations-v2/terrain/bons-livraison/${blId}`);
export const terrainSoumettreBLV2       = (blId, data) => API.post(`/api/locations-v2/terrain/bons-livraison/${blId}/soumettre`, data);
export const soumettreControlesV2       = (blId, data) => API.post(`/api/locations-v2/bls/${blId}/controles`, data);
export const getControlesV2             = (blId) => API.get(`/api/locations-v2/bls/${blId}/controles`);
export const uploadPhotoTerrain         = (data) => API.post('/api/locations-v2/terrain/upload-photo', data);

export const getContrats = (params) => API.get('/api/locations/contrats', { params });
export const getAvenants = (parentId) => API.get('/api/locations/contrats', { params: { parent_id: parentId } });
export const migrateAvenantsJsonb = () => API.post('/api/locations/contrats/migrate-avenants-jsonb');

export const getContratsStats = () => API.get('/api/locations/contrats/stats');
export const getContrat = (id) => API.get(`/api/locations/contrats/${id}`);
export const getContratPdf = (id) => API.get(`/api/locations/contrats/${id}/pdf`, { responseType: 'blob' });
export const envoyerEmailContratDevis = (id, data) => API.post(`/api/locations/contrats/${id}/envoyer-devis`, data);
export const createContrat = (data) => API.post('/api/locations/contrats', data);
export const updateContrat = (id, data) => API.patch(`/api/locations/contrats/${id}`, data);
export const patchTarifLigne = (contratId, seq, data) => API.patch(`/api/locations/contrats/${contratId}/lignes/${seq}/tarif`, data);
export const patchDatesEffectives = (id, data) => API.patch(`/api/locations/contrats/${id}/dates-effectives`, data);
export const patchLigneServiceFlags = (contratId, ligneId, data) => API.patch(`/api/locations/contrats/${contratId}/ligne-service/${ligneId}`, data);
export const getClLignesDates = (contratId) => API.get(`/api/locations/contrats/${contratId}/cl-lignes`);
export const patchClLigneDates = (ligneId, data) => API.patch(`/api/locations/lignes/${ligneId}/dates-effectives`, data);
export const deleteContrat = (id) => API.delete(`/api/locations/contrats/${id}`);
export const transitionContrat = (id, data) => API.post(`/api/locations/contrats/${id}/transition`, data);
export const createAvenant = (id, data) => API.post(`/api/locations/contrats/${id}/avenants`, data);
export const addSuspension = (id, data) => API.post(`/api/locations/contrats/${id}/suspensions`, data);
export const deleteSuspension = (id, suspensionId) => API.delete(`/api/locations/contrats/${id}/suspensions/${suspensionId}`);
export const patchValidationContrat = (id, data) => API.patch(`/api/locations/contrats/${id}/validation`, data);
export const uploadDocumentContrat = (id, typeDoc, formData) =>
  API.post(`/api/locations/contrats/${id}/upload-document?type_doc=${typeDoc}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const envoyerEnSignature = (id, data) => API.post(`/api/locations/contrats/${id}/envoi-signature`, data);
export const annulerSignatureTerrain = (id) => API.delete(`/api/locations/contrats/${id}/envoi-signature`);
export const envoyerSignatureTerrain = (id, data) => API.post(`/api/locations/contrats/${id}/envoi-signature`, data);
export const signerContratTerrain = (id, data) => API.post(`/api/locations/contrats/${id}/signer-terrain`, data);
export const envoyerSignatureMail = (id, data) => API.post(`/api/locations/contrats/${id}/envoi-signature-mail`, data);
export const signatureRecue = (id, formData) => API.post(`/api/locations/contrats/${id}/signature-recue`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const envoyerSignatureLien = (id, data) => API.post(`/api/locations/contrats/${id}/envoi-signature-lien`, data);
export const annulerLienSignatureLocation = (id) => API.post(`/api/locations/contrats/${id}/annuler-lien-signature`);
export const getSignatureWeb = (token) => API.get(`/api/locations/signature-web/${token}`);
export const soumettreSignatureWeb = (token, data) => API.post(`/api/locations/signature-web/${token}`, data);
export const getContratsEnAttenteSignature = () => API.get('/api/locations/contrats/terrain/en-attente');
export const getPersonnesEntreprise = (entreprise_id) => API.get('/api/personnes', { params: { entreprise_id } });

// Jours fériés par agence
export const getJoursFeries = (agenceId) => API.get(`/api/parametres/agences/${agenceId}/jours-feries`);
export const setJoursFeries = (agenceId, data) => API.put(`/api/parametres/agences/${agenceId}/jours-feries`, data);
export const getNumerotation = () => API.get('/api/parametres/numerotation');
export const setNumerotation = (data) => API.put('/api/parametres/numerotation', data);

// Conditions de paiement
export const getConditionsPaiement = () => API.get('/api/parametres/conditions-paiement');
export const setConditionsPaiement = (data) => API.put('/api/parametres/conditions-paiement', data);
export const getConditionsPaiementReferentiel = () => API.get('/api/parametres/conditions-paiement-referentiel');
export const createConditionPaiement  = (data)        => API.post('/api/parametres/conditions-paiement-referentiel', data);
export const updateConditionPaiement  = (id, data)    => API.put(`/api/parametres/conditions-paiement-referentiel/${id}`, data);
export const deleteConditionPaiement  = (id)          => API.delete(`/api/parametres/conditions-paiement-referentiel/${id}`);
// getProviderTerms / syncConditionsPaiement / updateConditionLiaisons supprimés (architecture simplifiée)

export const getOdooPaymentTerms = () => API.get('/api/odoo/payment-terms');
export const patchEntrepriseConditions = (id, data) => API.patch(`/api/entreprises/${id}/conditions-commerciales`, data);

// Types de documents
export const getTypesDocuments = () => API.get('/api/types-documents');
export const updateTypeDocument = (id, data) => API.put(`/api/types-documents/${id}`, data);

// Documents de contrat
export const getContratDocuments = (contratId) => API.get(`/api/locations/contrats/${contratId}/documents`);
export const uploadContratDocument = (contratId, formData) =>
  API.post(`/api/locations/contrats/${contratId}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteContratDocument = (contratId, docId) => API.delete(`/api/locations/contrats/${contratId}/documents/${docId}`);
// Nouvelles fonctions pour DocumentsTab (endpoint entity_documents)
export const getContratDocumentsTab = (contratId) => API.get(`/api/contrats/${contratId}/documents`);
export const uploadContratDocumentTab = (contratId, formData) =>
  API.post(`/api/contrats/${contratId}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const patchContratDocumentTab = (contratId, docId, data) => API.patch(`/api/contrats/${contratId}/documents/${docId}`, data);
export const deleteContratDocumentTab = (contratId, docId) => API.delete(`/api/contrats/${contratId}/documents/${docId}`);
export const downloadContratDocumentTab = (contratId, docId) => API.get(`/api/contrats/${contratId}/documents/${docId}/download`, { responseType: 'blob' });

// Upload types
export const getUploadTypes = () => API.get('/api/upload-types');
export const createUploadType = (data) => API.post('/api/upload-types', data);
export const updateUploadType = (id, data) => API.put(`/api/upload-types/${id}`, data);
export const deleteUploadType = (id) => API.delete(`/api/upload-types/${id}`);

// Devis documents
export const getDevisDocuments = (venteId) => API.get(`/api/devis/${venteId}/documents`);
export const getMaterielDocuments    = (id)         => API.get(`/api/materiels/${id}/documents`);
export const uploadMaterielDocument  = (id, form)   => API.post(`/api/materiels/${id}/documents`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteMaterielDocument  = (id, docId)  => API.delete(`/api/materiels/${id}/documents/${docId}`);
export const patchMaterielDocument   = (id, docId, d) => API.patch(`/api/materiels/${id}/documents/${docId}`, d);
export const downloadMaterielDocument = (id, docId) => API.get(`/api/materiels/${id}/documents/${docId}/download`, { responseType: 'blob' });
export const uploadDevisDocument = (venteId, formData) =>
  API.post(`/api/devis/${venteId}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const patchDevisDocument = (venteId, docId, data) => API.patch(`/api/devis/${venteId}/documents/${docId}`, data);
export const deleteDevisDocument = (venteId, docId) => API.delete(`/api/devis/${venteId}/documents/${docId}`);
export const downloadDevisDocument = (venteId, docId) => API.get(`/api/devis/${venteId}/documents/${docId}/download`, { responseType: 'blob' });

// Réparation documents
export const getReparationDocuments = (ordreId) => API.get(`/api/reparations/${ordreId}/documents`);
export const uploadReparationDocument = (ordreId, formData) =>
  API.post(`/api/reparations/${ordreId}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const patchReparationDocument = (ordreId, docId, data) => API.patch(`/api/reparations/${ordreId}/documents/${docId}`, data);
export const deleteReparationDocument = (ordreId, docId) => API.delete(`/api/reparations/${ordreId}/documents/${docId}`);
export const downloadReparationDocument = (ordreId, docId) => API.get(`/api/reparations/${ordreId}/documents/${docId}/download`, { responseType: 'blob' });

// Entreprise documents
export const getEntrepriseDocuments = (entrepriseId) => API.get(`/api/entreprises/${entrepriseId}/documents`);
export const uploadEntrepriseDocument = (entrepriseId, formData) =>
  API.post(`/api/entreprises/${entrepriseId}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const patchEntrepriseDocument = (entrepriseId, docId, data) => API.patch(`/api/entreprises/${entrepriseId}/documents/${docId}`, data);
export const deleteEntrepriseDocument = (entrepriseId, docId) => API.delete(`/api/entreprises/${entrepriseId}/documents/${docId}`);
export const downloadEntrepriseDocument = (entrepriseId, docId) => API.get(`/api/entreprises/${entrepriseId}/documents/${docId}/download`, { responseType: 'blob' });

// Toggle sans bon de commande
export const getEntitesSansBonCommande = (module) => API.get('/api/documents/sans-bon-commande', { params: { module } });
// Critères de contrôle
export const getCriteres = () => API.get('/api/locations/criteres');
export const createCritere = (data) => API.post('/api/locations/criteres', data);
export const updateCritere = (id, data) => API.put(`/api/locations/criteres/${id}`, data);
export const deleteCritere = (id) => API.delete(`/api/locations/criteres/${id}`);

// Modèles de contrôle
export const getModelesControle = () => API.get('/api/locations/modeles-controle');
export const createModeleControle = (data) => API.post('/api/locations/modeles-controle', data);
export const updateModeleControle = (id, data) => API.put(`/api/locations/modeles-controle/${id}`, data);
export const deleteModeleControle = (id) => API.delete(`/api/locations/modeles-controle/${id}`);
export const dupliquerModeleControle = (id) => API.post(`/api/locations/modeles-controle/${id}/dupliquer`);

// Bons de livraison
export const declencherLivraison = (contratId, materielId = null) =>
  API.post(`/api/locations/contrats/${contratId}/declencher-livraison${materielId ? `?materiel_id=${encodeURIComponent(materielId)}` : ''}`);
export const updateLignesMateriels = (contratId, lignes) => API.put(`/api/locations/contrats/${contratId}/lignes-materiels`, { lignes_materiels: lignes });
export const declencherRetour = (contratId, materielId = null) =>
  API.post(`/api/locations/contrats/${contratId}/declencher-retour${materielId ? `?materiel_id=${encodeURIComponent(materielId)}` : ''}`);
export const getBonsLivraisonContrat = (contratId) => API.get(`/api/locations/contrats/${contratId}/bons-livraison`);
export const getBLPdf = (blId) => API.get(`/api/locations/bons-livraison/${blId}/pdf`, { responseType: 'blob' });
export const listBonsLivraison = (params = {}) => API.get('/api/locations/bons-livraison', { params });
export const terrainListBonsLivraison = () => API.get('/api/locations/terrain/bons-livraison');
export const terrainGetBL = (blId) => API.get(`/api/locations/terrain/bons-livraison/${blId}`);
export const terrainSoumettresBL = (blId, data) => API.post(`/api/locations/terrain/bons-livraison/${blId}/soumettre`, data);
export const terrainChangerMateriel = (blId, materielId) => API.patch(`/api/locations/terrain/bons-livraison/${blId}/changer-materiel`, { materiel_id: materielId });
export const getPersonnesParFonction = (fonction) => API.get('/api/personnes', { params: { fonction, per_page: 200 } });

// ── Livraisons ────────────────────────────────────────────────────────────────
export const getLivraisons = (params = {}) => API.get('/api/locations/livraisons', { params });
export const getPlanningDisponibilite = (params = {}) => API.get('/api/locations/planning-disponibilite', { params });
export const getArchitecture = () => API.get('/api/admin/architecture');
export const getAdminLogs    = (n = 200, level = 'ALL') => API.get('/api/admin/logs-realtime', { params: { n, level } });
export const clearAdminLogs  = () => API.delete('/api/admin/logs-realtime');

// ── Rapports ──────────────────────────────────────────────────────────────────
export const getPotentielLocatif    = (params)       => API.get('/api/rapports/potentiel-locatif', { params });
export const getBalanceAgee         = (params)       => API.get('/api/rapports/balance-agee', { params });
export const getBalanceAgeeContacts = (entrepriseId) => API.get(`/api/rapports/balance-agee/contacts/${entrepriseId}`);
export const getPalmaresClients         = (params)               => API.get('/api/rapports/palmares-clients', { params });
export const getPalmaresClientMensuel   = (entrepriseId, params)  => API.get(`/api/rapports/palmares-clients/${entrepriseId}/mensuel`, { params });
export const getPalmaresContacts        = (entrepriseId)          => API.get(`/api/rapports/palmares-clients/contacts/${entrepriseId}`);
export const getAppetencesLocation      = (entrepriseId)          => API.get(`/api/rapports/appetences/location/${entrepriseId}`);
export const getDashboardRecouvrement = ()      => API.get('/api/dashboard/recouvrement');

// ══════════════════════════════════════════════════════════════
// ── Ressources Humaines ───────────────────────────────────────
// ══════════════════════════════════════════════════════════════

// Employés
export const getEmployes = (params = {}) => API.get('/api/rh/employes', { params });
export const getEmploye = (id) => API.get(`/api/rh/employes/${id}`);
export const createEmploye = (data) => API.post('/api/rh/employes', data);
export const updateEmploye = (id, data) => API.patch(`/api/rh/employes/${id}`, data);
export const getFonctionsInternes = () => API.get('/api/rh/fonctions-internes');
export const deleteEmploye = (id) => API.delete(`/api/rh/employes/${id}`);

// Contrats de travail
export const addContratTravail = (employeId, data) => API.post(`/api/rh/employes/${employeId}/contrats-travail`, data);
export const updateContratTravail = (ctId, data) => API.patch(`/api/rh/contrats-travail/${ctId}`, data);

// Types TNF
export const getTypesTNF = () => API.get('/api/rh/types-tnf');
export const createTypeTNF = (data) => API.post('/api/rh/types-tnf', data);
export const updateTypeTNF = (id, data) => API.patch(`/api/rh/types-tnf/${id}`, data);
export const deleteTypeTNF = (id) => API.delete(`/api/rh/types-tnf/${id}`);

// Temps Non Facturables
export const getTNF = (params = {}) => API.get('/api/rh/tnf', { params });
export const createTNF = (data) => API.post('/api/rh/tnf', data);
export const updateTNF = (id, data) => API.patch(`/api/rh/tnf/${id}`, data);
export const deleteTNF = (id) => API.delete(`/api/rh/tnf/${id}`);

// OR — Affectations techniciens
export const updateUserEmployeId = (userId, employeId) => API.patch(`/api/users/${userId}/employe`, { employe_id: employeId });
export const getTerrainMesOrs = (semaine) => API.get('/api/terrain/atelier', { params: { semaine } });
export const getORTechniciens = (params = {}) => API.get('/api/rh/or-techniciens', { params });
export const createORTechnicien = (data) => API.post('/api/rh/or-techniciens', data);
export const updateORTechnicien = (id, data) => API.patch(`/api/rh/or-techniciens/${id}`, data);
export const deleteORTechnicien = (id) => API.delete(`/api/rh/or-techniciens/${id}`);

// Tableau de charge
export const getTableauDeCharge = (semaine) => API.get('/api/rh/tableau-de-charge', { params: { semaine } });

// Distribution — Ventes
export const getVentes        = (p = {})        => API.get('/api/ventes', { params: p });
export const createVente      = (data)           => API.post('/api/ventes', data);
export const getVente         = (id)             => API.get(`/api/ventes/${id}`);
export const updateVente      = (id, data)       => API.put(`/api/ventes/${id}`, data);
export const updateAcheteurExploitantBC = (id, data) => API.patch(`/api/ventes/${id}/acheteur-exploitant`, data);
export const changeStatutVente = (id, statut)    => API.patch(`/api/ventes/${id}/statut`, { statut });
export const deleteVente       = (id)             => API.delete(`/api/ventes/${id}`);
export const dupliquerDevis    = (id, agenceId)  => API.post(`/api/ventes/${id}/dupliquer`, agenceId ? { agence_id: agenceId } : {});
export const facturerBC                = (id, payload)    => API.post(`/api/ventes/${id}/facturer`, payload || {});
export const listerFacturesBC          = (id)             => API.get(`/api/ventes/${id}/factures`);
export const supprimerFactureDraft    = (id)             => API.delete(`/api/ventes/${id}/pennylane-draft`);
export const supprimerFactureOdooDraft = (id)             => API.delete(`/api/ventes/${id}/pennylane-draft`);
export const creerBLDepuisFacture = (vente_id, data) => API.post(`/api/ventes/${vente_id}/bl`, data);
export const validerPaiementBC    = (id)            => API.patch(`/api/ventes/${id}/valider-paiement`);
export const getPlDraftPdfBlob    = (id)            => API.get(`/api/ventes/${id}/pennylane-draft-pdf/view`, { responseType: 'blob' });
export const getPlInvoicePdfBlob  = (id)            => API.get(`/api/ventes/${id}/pennylane-pdf/view`, { responseType: 'blob' });
export const validerPLDraft       = (id)            => API.post(`/api/ventes/${id}/pennylane-valider`);

// ── Règlements ────────────────────────────────────────────────────────────────
export const getModesReglement    = ()              => API.get('/api/reglements/modes');
export const getReglements        = (commandeId)   => API.get(`/api/ventes/${commandeId}/reglement`);
export const getReglement         = (commandeId)   => API.get(`/api/ventes/${commandeId}/reglement`);
export const createReglement      = (commandeId, data) => API.post(`/api/ventes/${commandeId}/reglement`, data);
export const deleteReglement      = (commandeId, reglementId) => API.delete(`/api/ventes/${commandeId}/reglement/${reglementId}`);
export const supprimerPLDraft     = (id)            => API.delete(`/api/ventes/${id}/pennylane-draft`);
export const nouvelleVersion   = (id)             => API.post(`/api/ventes/${id}/nouvelle-version`);
export const signerTabletteVente = (id, data)     => API.post(`/api/ventes/${id}/signature/tablette`, data);
export const envoyerSignatureTerrainVente = (id, data) => API.post(`/api/ventes/${id}/signature/terrain`, data);
export const annulerSignatureTerrainVente = (id)   => API.post(`/api/ventes/${id}/signature/annuler`);
export const signerVenteTerrain = (id, data)       => API.post(`/api/ventes/${id}/signature/signer-terrain`, data);
export const getDevisVenteEnAttenteSignature = ()  => API.get('/api/ventes/terrain/en-attente');
export const envoyerBCEmail      = (id, data)     => API.post(`/api/ventes/${id}/signature/email`, data);
export const uploadBCSigne       = (id, formData) => API.post(`/api/ventes/${id}/signature/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' }});
export const annulerLienSignature  = (id)         => API.post(`/api/ventes/${id}/signature/annuler`);
export const envoyerLienSignature= (id, data)     => API.post(`/api/ventes/${id}/signature/lien`, data);
// Devis actions
export const getDevisPdf      = (id)             => API.get(`/api/ventes/${id}/pdf`, { responseType: 'blob' });
export const getOrPdf = (id, type_doc = 'or') => API.get(`/api/atelier/ordres/${id}/pdf?type_doc=${type_doc}`, { responseType: 'blob' });
export const imprimerDevis    = (id)             => API.post(`/api/ventes/${id}/imprimer`);
export const envoyerEmailDevis= (id, data)       => API.post(`/api/ventes/${id}/envoyer-email`, data);
export const getDevisRevisions= (id)             => API.get(`/api/ventes/${id}/revisions`);
export const getVenteEcheances   = (id)          => API.get(`/api/ventes/${id}/echeances`);
export const createVenteEcheance = (id, data)    => API.post(`/api/ventes/${id}/echeances`, data);
export const updateVenteEcheance = (id, eid, d)  => API.patch(`/api/ventes/${id}/echeances/${eid}`, d);
export const deleteVenteEcheance = (id, eid)     => API.delete(`/api/ventes/${id}/echeances/${eid}`);
export const rappelerRevision = (id, revId)      => API.post(`/api/ventes/${id}/revisions/${revId}/rappeler`);
export const getDevisActivite = (id)             => API.get(`/api/ventes/${id}/activite`);

// Email templates
export const getEmailContextes        = ()              => API.get('/api/email-templates/contextes');
export const getEmailContextesSysteme = ()              => API.get('/api/email-templates/systeme/contextes');
export const getEmailTemplates              = ()              => API.get('/api/email-templates');
export const getEmailTemplate               = (contexte)      => API.get(`/api/email-templates/${contexte}`);
export const upsertEmailTemplate            = (contexte, data)=> API.put(`/api/email-templates/${contexte}`, data);
export const deleteEmailTemplate            = (contexte)      => API.delete(`/api/email-templates/${contexte}`);
export const upsertEmailTemplateSysteme     = (typeCode, data)=> API.put(`/api/email-templates/systeme/${typeCode}`, data);
export const envoyerFactureEmailVente       = (venteId, payload)   => API.post(`/api/ventes/${venteId}/envoyer-facture`, payload);
export const envoyerFactureEmailAtelier     = (ordreId, payload)   => API.post(`/api/atelier/${ordreId}/envoyer-facture`, payload);
export const envoyerFactureEmailLocation    = (contratId, payload) => API.post(`/api/locations/${contratId}/envoyer-facture`, payload);
export const getDestinatairesFactureVente   = (venteId)            => API.get(`/api/ventes/${venteId}/destinataires-facture`);
export const getDestinatairesFactureAtelier = (ordreId)            => API.get(`/api/atelier/${ordreId}/destinataires-facture`);
export const getDestinatairesFactureLocation = (contratId)         => API.get(`/api/locations/${contratId}/destinataires-facture`);

// Bons de Livraison Ventes
export const getBLVentes     = (p = {})     => API.get('/api/bl-ventes', { params: p });
export const getBLVente      = (id)         => API.get(`/api/bl-ventes/${id}`);
export const creerBLDepuisDevis = (venteId, data) => API.post(`/api/ventes/${venteId}/bl`, data);
export const updateBLVente   = (id, data)   => API.put(`/api/bl-ventes/${id}`, data);
export const livrerBL        = (id)         => API.patch(`/api/bl-ventes/${id}/livrer`);
export const annulerBL       = (id)         => API.patch(`/api/bl-ventes/${id}/annuler`);
export const facturerBLOdoo       = (id)     => API.post(`/api/bl-ventes/${id}/facturer-odoo`);
export const facturerBLPennylane  = (id)     => API.post(`/api/pennylane/bl-ventes/${id}/facturer`);
export const confirmerPaiementBL  = (id)     => API.patch(`/api/bl-ventes/${id}/paiement-recu`);
// Factures distribution
export const getFacturesDistribution   = (p = {}) => API.get('/api/factures-distribution', { params: p });
export const getFactureDistributionPdf   = (blId)   => API.get(`/api/factures-distribution/${blId}/pdf`, { responseType: 'arraybuffer' });
export const syncFactureDistributionPl   = (id)     => API.post(`/api/factures-distribution/${id}/sync-pl`);
export const syncAllFacturesDistributionPl = ()     => API.post('/api/factures-distribution/sync-pl-all');
export const getFactureLocationPdf     = (id)     => API.get(`/api/locations/factures/${id}/pdf`,        { responseType: 'arraybuffer' });
export const getDevisParToken     = (token)  => API.get(`/api/ventes/signer/${token}`);
export const signerDevisWeb       = (token, data) => API.post(`/api/ventes/signer/${token}`, data);

// Intégrations tierces
export const getIntegrations         = ()                    => API.get('/api/integrations');
export const getIntegrationProviders = ()                    => API.get('/api/integrations/providers');
export const getPennyLaneCompanies   = ()                    => API.get('/api/integrations/pennylane/companies');
export const saveIntegration         = (provider, data)      => API.put(`/api/integrations/${provider}`, data);
export const deleteIntegration       = (provider)            => API.delete(`/api/integrations/${provider}`);
export const testIntegration         = (provider, data)      => API.post(`/api/integrations/${provider}/test`, data);
// Routage comptable par société
export const getIntegrationRouting   = ()                    => API.get('/api/integrations/routing');
export const updateIntegrationRouting = (societe_id, data)   => API.put(`/api/integrations/routing/${societe_id}`, data);
// Mappings TVA par logiciel comptable
export const getTvaMappings           = (provider = 'pennylane') => API.get(`/api/tva/mappings?provider=${provider}`);
export const saveTvaMappings          = (data)               => API.put('/api/tva/mappings', data);
export const deleteTvaMapping         = (id)                 => API.delete(`/api/tva/mappings/${id}`);

// Clés API publiques
export const getApiKeys    = ()          => API.get('/api/api-keys');
export const getApiScopes  = ()          => API.get('/api/api-keys/scopes');
export const createApiKey  = (data)      => API.post('/api/api-keys', data);
export const toggleApiKey  = (id)        => API.patch(`/api/api-keys/${id}/toggle`);
export const deleteApiKey  = (id)        => API.delete(`/api/api-keys/${id}`);

// ── Pappers ──────────────────────────────────────────────────────────────────
export const getEntreprisePdf   = (id)     => API.get(`/api/entreprises/${id}/pdf`, { responseType: 'arraybuffer' });
export const getEntrepriseRelevePdf = (id) => API.get(`/api/entreprises/${id}/releve-compte`, { responseType: 'arraybuffer' });
export const getRelevePdfTemplateMail = (id) => API.get(`/api/entreprises/${id}/releve-compte/template-mail`);
export const envoyerRelevePdfMail = (id, data) => API.post(`/api/entreprises/${id}/releve-compte/envoyer-mail`, data);
export const pappersSearch      = (q)     => API.get('/api/pappers/suggestions', { params: { q } });
export const pappersEntreprise  = (siren) => API.get(`/api/pappers/entreprise/${siren}`);

// ── Reminders ────────────────────────────────────────────────────────────────
export const getReminders    = (params) => API.get('/api/reminders', { params });
export const createReminder  = (data)   => API.post('/api/reminders', data);
export const patchReminder   = (id, d)  => API.patch(`/api/reminders/${id}`, d);
export const deleteReminder  = (id)     => API.delete(`/api/reminders/${id}`);

// ── Mailing ───────────────────────────────────────────────────────────────────
export const getMailingTypes        = ()              => API.get('/api/mailing/types');
export const createMailingType      = (data)          => API.post('/api/mailing/types', data);
export const updateMailingType      = (id, data)      => API.put(`/api/mailing/types/${id}`, data);
export const deleteMailingType      = (id)            => API.delete(`/api/mailing/types/${id}`);
export const toggleCampagneActif    = (id)            => API.patch(`/api/mailing/types/${id}/toggle-actif`);
export const scheduleCampagne       = (id, scheduled_at) => API.post(`/api/mailing/types/${id}/schedule`, { scheduled_at });
export const pauseCampagne          = (id)            => API.post(`/api/mailing/types/${id}/pause`);
export const resumeCampagne         = (id)            => API.post(`/api/mailing/types/${id}/resume`);
export const cancelCampagne         = (id)            => API.post(`/api/mailing/types/${id}/cancel`);
export const sendNowCampagne        = (id)            => API.post(`/api/mailing/types/${id}/send-now`);
export const getMailingAbonnes      = (mid)           => API.get(`/api/mailing/types/${mid}/abonnes`);
export const addMailingAbonne       = (mid, data)     => API.post(`/api/mailing/types/${mid}/abonnes`, data);
export const removeMailingAbonne    = (mid, aid)      => API.delete(`/api/mailing/types/${mid}/abonnes/${aid}`);
export const getMailingEnvois       = (params)        => API.get('/api/mailing/envois', { params });
export const envoyerMailing         = (data)          => API.post('/api/mailing/envoyer', data);
export const previewMailingPdf      = (mid, date_fin) => API.get(`/api/mailing/types/${mid}/preview-pdf`, { params: { date_fin }, responseType: 'blob' });

// ── Email Audiences ───────────────────────────────────────────────────────────
export const getEmailAudiences      = ()              => API.get('/api/email/audiences');
export const createEmailAudience    = (data)          => API.post('/api/email/audiences', data);
export const updateEmailAudience    = (id, data)      => API.put(`/api/email/audiences/${id}`, data);
export const deleteEmailAudience    = (id)            => API.delete(`/api/email/audiences/${id}`);
export const getAudienceMembres     = (id)            => API.get(`/api/email/audiences/${id}/membres`);
export const getAudiencePreview     = (id)            => API.get(`/api/email/audiences/${id}/preview`);
export const getAudienceLignes      = (id)            => API.get(`/api/email/audiences/${id}/lignes`);

// ── Email Templates Campagne ─────────────────────────────────────────────────
export const getCampagneTemplates    = ()          => API.get('/api/email-templates/campagne');
export const createCampagneTemplate  = (data)      => API.post('/api/email-templates/campagne', data);
export const updateCampagneTemplate  = (id, data)  => API.put(`/api/email-templates/campagne/${id}`, data);
export const duplicateCampagneTemplate = (id)        => API.post(`/api/email-templates/campagne/${id}/duplicate`);
export const deleteCampagneTemplate  = (id)        => API.delete(`/api/email-templates/campagne/${id}`);

// ── PDF Generators ────────────────────────────────────────────────────────────
export const getPdfGenerators        = ()          => API.get('/api/pdf-generators');
export const createPdfGenerator      = (data)      => API.post('/api/pdf-generators', data);
export const updatePdfGenerator      = (id, data)  => API.put(`/api/pdf-generators/${id}`, data);
export const deletePdfGenerator      = (id)        => API.delete(`/api/pdf-generators/${id}`);
export const getTemplatePdfAttachments = (tid)     => API.get(`/api/email-templates/${tid}/pdf-attachments`);
export const setTemplatePdfAttachments = (tid, ids) => API.put(`/api/email-templates/${tid}/pdf-attachments`, { pdf_generator_ids: ids });

// ── Email Logs ────────────────────────────────────────────────────────────────
export const getEmailLogs              = (params)    => API.get('/api/email/logs', { params });
export const getRelancesSnapshot       = (params)    => API.get('/api/relances/snapshot', { params });
export const getRelancesConfig              = ()              => API.get('/api/relances/config');
export const getRelancesConfigCampaigns     = ()              => API.get('/api/relances/config/campaigns');
export const updateRelanceConfig            = (sid, body)     => API.put(`/api/relances/config/${sid}`, body);
export const updateRelanceConfigByCampaign  = (cid, body)     => API.put(`/api/relances/config/by-campaign/${cid}`, body);
export const getRelancesCampaigns      = ()          => API.get('/api/relances/campaigns');
export const getRelancesDossiers       = (params)    => API.get('/api/relances/dossiers', { params });
export const getRelanceDossierDetail   = (id)        => API.get(`/api/relances/dossiers/${id}/detail`);
export const bulkSendRelances          = (body)      => API.post('/api/relances/bulk-send', body);
export const forcerRelance             = (id)        => API.post(`/api/relances/dossiers/${id}/forcer-relance`);
export const ajouterDestRelance        = (id, body)  => API.post(`/api/relances/dossiers/${id}/ajouter-destinataire`, body);
export const patchDossierNotes         = (id, notes) => API.patch(`/api/relances/dossiers/${id}/notes`, { notes });
export const gelerFacture              = (id, body)  => API.post(`/api/relances/dossiers/${id}/geler-facture`, body);
export const reintegrerFacture         = (gelId)     => API.post(`/api/relances/factures-gelees/${gelId}/reintegrer`);
export const triggerRelanceSync        = ()          => API.post('/api/relances/sync');

// ── Discussions non-lus ───────────────────────────────────────────────────────
export const getDiscussionsUnread   = ()              => API.get('/api/discussions/unread');
export const markDiscussionRead     = (thread_id)     => API.post(`/api/discussions/${thread_id}/mark-read`);

// ─── Bons de retour ventes ────────────────────────────────────────────────────
export const getRetoursVentes   = (p = {})   => API.get('/api/retours-ventes', { params: p });
export const getRetourVente     = (id)       => API.get(`/api/retours-ventes/${id}`);
export const creerRetourVente   = (data)     => API.post('/api/retours-ventes', data);
export const updateRetourVente  = (id, data) => API.put(`/api/retours-ventes/${id}`, data);
export const validerRetourVente = (id)       => API.patch(`/api/retours-ventes/${id}/valider`);
export const annulerRetourVente = (id)       => API.patch(`/api/retours-ventes/${id}/annuler`);
export const avoirOdooRetour    = (id)       => API.post(`/api/retours-ventes/${id}/avoir-odoo`);

// ─── Transferts inter-agences ─────────────────────────────────────────────────
export const getTransferts         = (p = {})   => API.get('/api/transferts', { params: p });
export const getTransfert          = (id)       => API.get(`/api/transferts/${id}`);
export const creerTransfert        = (data)     => API.post('/api/transferts', data);
export const updateTransfert       = (id, data) => API.patch(`/api/transferts/${id}`, data);
export const expedierTransfert     = (id, data) => API.patch(`/api/transferts/${id}/expedier`, data);
export const receptionnerTransfert = (id, data) => API.patch(`/api/transferts/${id}/receptionner`, data);
export const annulerTransfert      = (id)       => API.patch(`/api/transferts/${id}/annuler`);
export const supprimerTransfert    = (id)       => API.delete(`/api/transferts/${id}`);

// ─── Exercices comptables ─────────────────────────────────────────────────────
export const getExercicesComptables    = ()         => API.get('/api/exercices-comptables');
export const createExerciceComptable   = (data)     => API.post('/api/exercices-comptables', data);
export const updateExerciceComptable   = (id, data) => API.put(`/api/exercices-comptables/${id}`, data);
export const deleteExerciceComptable   = (id)       => API.delete(`/api/exercices-comptables/${id}`);

// ─── Signature V2 ──────────────────────────────────────────────────────────────
export const envoyerSignatureTerrainV2    = (id, data) => API.post(`/api/locations-v2/contrats/${id}/envoi-signature`, data);
export const annulerSignatureTerrainV2   = (id)       => API.delete(`/api/locations-v2/contrats/${id}/envoi-signature`);
export const signerContratTerrainV2      = (id, data) => API.post(`/api/locations-v2/contrats/${id}/signer-terrain`, data);
export const envoyerSignatureMailV2      = (id, data) => API.post(`/api/locations-v2/contrats/${id}/envoi-signature-mail`, data);
export const envoyerSignatureLienV2      = (id, data) => API.post(`/api/locations-v2/contrats/${id}/envoi-signature-lien`, data);
export const signatureRecueV2            = (id, data) => API.post(`/api/locations-v2/contrats/${id}/signature-recue`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
export const annulerLienSignatureV2      = (id)       => API.post(`/api/locations-v2/contrats/${id}/annuler-lien-signature`);
export const getSignatureWebV2           = (token)    => API.get(`/api/locations-v2/signature-web/${token}`);
export const soumettreSignatureWebV2     = (token, d) => API.post(`/api/locations-v2/signature-web/${token}`, d);
export const getContratV2PDF             = (id)       => API.get(`/api/locations-v2/contrats/${id}/pdf`, { responseType: 'blob' });


export default API;

// Auto-generated stub for missing getAudiences export
export const getAudiences = async () => {
  try {
    const response = await api.get('/api/audiences');
    return response.data;
  } catch (error) {
    console.error('Error fetching audiences:', error);
    return [];
  }
};
